import React, { useEffect, useRef, useState, useMemo } from 'react';
import ReactDOM from 'react-dom';
import ConfirmationDialog from '../../../../../../CommonComponents/confirmation_dialog/ConfirmationDialog';
import { useParams } from 'react-router-dom';
import CustomToolTip from '../../../../../../CommonComponents/CustomToolTip';
import MissionService from '../../../../../../../service/MissionService.js';
import { showToastMessage } from '../../../../../../../utils/commonFunctions.js';
import { useMDCockpit } from '../../../../hooks/useMDCockpit';
import { useCesium } from '../../../../context/CesiumContext';
import { useCesiumClock } from '../../../../hooks/useCesiumClock';
import { mapAccessAnalysisReportsResponse, getReportUsedEntities } from '../../../../lib/accessAnalysisReports';
import type { AccessAnalysisReport, CoverageAnalysisReport, GeoFeature, GroundStation, Satellite, SatGroup } from '../../../../types/mission';
import type { ReportHoverHighlights } from '../../../../types/mdai';
import styles from './AccessAnalysisReportsPanel.module.scss';
import { CoverageHeatmapLegendPanel, type HeatmapMetricMode } from './CoverageHeatmapLegendPanel';
import { ResponsiveContainer, AreaChart, Area, XAxis, YAxis, Tooltip, CartesianGrid, LineChart, Line, Legend, ReferenceLine } from 'recharts';
import CustomFilter from '../../../../../../CommonComponents/custom_filter/CustomFilter';
import { FilterSvg } from '../../../../../../CommonComponents/svgs/Svgs';
import type { AllFilterType, FilterListType } from '../../../../../../CommonComponents/custom_filter/types';

type ReportTab = 'Summary' | 'Events' | 'Plots';
const REPORT_TABS: ReportTab[] = ['Summary', 'Events', 'Plots'];
const EVENTS_PAGE_SIZE = 30;
const REPORT_PANEL_MIN_WIDTH = 336;
const REPORT_PANEL_MAX_WIDTH = 900;
const AI_PLACEHOLDER_PROMPTS = [
  'Ask Antaris INTELLIGENCE to summarize this report',
  'Find the highest value access windows',
  'Compare satellite and ground station coverage',
  'Explain the strongest target passes',
];
const AI_LOADING_MESSAGES = [
  'Reading the access windows...',
  'Tracing satellite-ground station geometry...',
  'Checking strongest passes...',
  'Looking for coverage gaps...',
  'Packaging the useful bits...',
];

type EntityNameMaps = {
  satellites: Map<string, string>;
  groundStations: Map<string, string>;
  geoFeatures: Map<string, string>;
};
type AIChatMessage = { role: 'user' | 'assistant'; content: string };
type AIMessageAction = 'copy' | 'delete' | 'edit' | 'resend';
type PlotSourceOption = { id: string; label: string; satelliteIds: string[]; type: 'group' | 'single' };
type PlotGroundStationOption = { id: string; label: string };
type AerPoint = { timeUtc?: any; azimuthDeg: number; elevationDeg: number; rangeKm?: number };
type InputSummaryCard = { key: string; count: number; icon: React.ReactNode; highlights: ReportHoverHighlights };

const DeleteIcon = () => (
  <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
    <path d="M3.2 4.1h9.6M6.4 2.2h3.2M4.8 4.1l.45 9.2h5.5l.45-9.2" stroke="currentColor" strokeWidth="1.1" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const CommandCoverageIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 17 17" fill="none">
    <path d="M0.5 11.4118V15.3528C0.5 15.9051 0.947716 16.3528 1.5 16.3528H5.47353M0.5 11.4118V5.8818M0.5 11.4118L5.47353 16.3528M7.9004 2.17373L3.31192 3.14529L1.29285 3.57281C0.830649 3.67068 0.5 4.07867 0.5 4.55112V5.8818M0.5 5.8818L11.04 16.3528M3.31192 3.14529L13.758 13.5231M15.666 4.35853L16.3439 1.10254C16.4175 0.749213 16.1039 0.436711 15.7508 0.51147L12.4889 1.20216L7.9004 2.17373M7.9004 2.17373L14.712 8.9408M12.4889 1.20216L15.666 4.35853M13.758 13.5231L14.712 8.9408L15.666 4.35853M13.758 13.5231L13.3346 15.5567C13.2381 16.0204 12.8293 16.3528 12.3556 16.3528H11.04M11.04 16.3528H5.47353" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const DownloadIcon = () => (
  <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
    <path d="M8 2.5v7M5.3 6.9 8 9.6l2.7-2.7M3.1 12.8h9.8" stroke="currentColor" strokeWidth="1.15" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const CopyIcon = () => (
  <svg width="13" height="13" viewBox="0 0 16 16" fill="none">
    <path d="M5.2 5.2h7.1v7.1H5.2zM3.7 10.8H2.9A1.2 1.2 0 0 1 1.7 9.6V3.7A1.2 1.2 0 0 1 2.9 2.5h5.9A1.2 1.2 0 0 1 10 3.7v.8" stroke="currentColor" strokeWidth="1.1" strokeLinejoin="round" />
  </svg>
);

const EditIcon = () => (
  <svg width="13" height="13" viewBox="0 0 16 16" fill="none">
    <path d="M3 11.7 3.4 9l6.8-6.8 2.6 2.6L6 11.6l-3 .1ZM9.4 3l2.6 2.6" stroke="currentColor" strokeWidth="1.1" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const ResendIcon = () => (
  <svg width="13" height="13" viewBox="0 0 16 16" fill="none">
    <path d="M12.7 5.2A5 5 0 1 0 13 9M12.7 5.2V2.4M12.7 5.2H9.9" stroke="currentColor" strokeWidth="1.1" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const DetachIcon = () => (
  <svg width="13" height="13" viewBox="0 0 16 16" fill="none">
    <path d="M6.1 3.2H3.2v9.6h9.6V9.9M9.3 3.2h3.5v3.5M12.5 3.5 8.1 7.9" stroke="currentColor" strokeWidth="1.1" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const AttachIcon = () => (
  <svg width="13" height="13" viewBox="0 0 16 16" fill="none">
    <path d="M9.9 12.8h2.9V3.2H3.2v2.9M6.7 12.8H3.2V9.3M3.5 12.5l4.4-4.4" stroke="currentColor" strokeWidth="1.1" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const CollapseIcon = () => (
  <svg width="13" height="13" viewBox="0 0 16 16" fill="none">
    <path d="M4 6.2 8 10l4-3.8" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const ReportPanelIcon = () => (
  <svg width="16" height="16" viewBox="0 0 18 18" fill="none">
    <path d="M1.76397 16.5334L15.3024 16.5334C15.9822 16.5334 16.5332 15.9824 16.5332 15.3027L16.5332 1.76422C16.5332 1.08448 15.9822 0.533447 15.3024 0.533447L1.76397 0.533447C1.08424 0.533447 0.533203 1.08448 0.533203 1.76422L0.533203 15.3027C0.533203 15.9824 1.08424 16.5334 1.76397 16.5334Z" stroke="currentColor" strokeWidth="1.06667" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M3.60938 3.61035H6.07091M3.60938 6.68727H9.14784M14.0709 6.68727L10.3786 12.8411L6.07091 10.3796L3.60938 14.0719" stroke="currentColor" strokeWidth="1.06667" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const AccessAnalysisIcon = () => (
  <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
    <path d="M15.6209 1.72694L14.7184 2.6295C13.3285 1.73979 11.7154 1.26121 10.0652 1.24903C8.41499 1.23684 6.79497 1.69153 5.39212 2.56062C3.98926 3.42971 2.86076 4.67777 2.13684 6.16076C1.41293 7.64376 1.12312 9.30123 1.30085 10.9419C1.47859 12.5825 2.11664 14.1395 3.14135 15.433C4.16606 16.7266 5.53567 17.704 7.0921 18.2525C8.64853 18.801 10.3283 18.8982 11.9376 18.5329C13.5469 18.1676 15.0202 17.3547 16.1872 16.1879C16.3051 16.0741 16.463 16.0111 16.6269 16.0125C16.7908 16.0139 16.9475 16.0797 17.0634 16.1956C17.1793 16.3114 17.2451 16.4682 17.2465 16.6321C17.2479 16.796 17.1849 16.9539 17.071 17.0718C15.729 18.4133 14.0329 19.3454 12.181 19.7591C10.329 20.1728 8.39753 20.0511 6.61215 19.4081C4.82678 18.7651 3.26119 17.6275 2.09822 16.128C0.935244 14.6285 0.222896 12.8291 0.0443639 10.9399C-0.134168 9.05066 0.228486 7.14965 1.08997 5.45885C1.95146 3.76805 3.27622 2.35725 4.90954 1.3912C6.54286 0.425159 8.41732 -0.0562488 10.314 0.00320139C12.2107 0.0626516 14.0513 0.660505 15.6209 1.72694ZM11.9519 5.39595L12.8908 4.45713C11.5665 3.76888 10.0398 3.57695 8.58649 3.916C7.13313 4.25505 5.84895 5.10273 4.96599 6.30589C4.08302 7.50904 3.65949 8.98832 3.77191 10.4765C3.88433 11.9646 4.5253 13.3635 5.579 14.4203C6.15943 15.0009 6.84854 15.4615 7.60699 15.7757C8.36543 16.0899 9.17835 16.2516 9.99931 16.2516C10.8203 16.2516 11.6332 16.0899 12.3916 15.7757C13.1501 15.4615 13.8392 15.0009 14.4196 14.4203C14.4777 14.3622 14.5238 14.2932 14.5553 14.2173C14.5867 14.1414 14.6029 14.06 14.6029 13.9778C14.6029 13.8956 14.5867 13.8142 14.5553 13.7383C14.5238 13.6624 14.4777 13.5934 14.4196 13.5353C14.3615 13.4771 14.2925 13.431 14.2166 13.3996C14.1406 13.3681 14.0593 13.352 13.9771 13.352C13.8949 13.352 13.8135 13.3681 13.7376 13.3996C13.6617 13.431 13.5927 13.4771 13.5346 13.5353C12.8929 14.1768 12.0889 14.6316 11.2085 14.8511C10.3282 15.0705 9.40469 15.0463 8.53705 14.781C7.6694 14.5157 6.89028 14.0194 6.28316 13.3451C5.67605 12.6709 5.26382 11.8442 5.09065 10.9536C4.91748 10.063 4.98989 9.14201 5.30012 8.2894C5.61036 7.43679 6.14672 6.68466 6.85175 6.11359C7.55678 5.54252 8.4039 5.17403 9.30234 5.04761C10.2008 4.92119 11.1167 5.0416 11.9519 5.39595ZM17.3173 2.68326C17.4345 2.80047 17.5003 2.95942 17.5003 3.12516C17.5003 3.2909 17.4345 3.44986 17.3173 3.56707L11.2069 9.67499C11.2784 9.94109 11.26 10.2233 11.1547 10.478C11.0494 10.7326 10.863 10.9453 10.6244 11.0831C10.3859 11.221 10.1085 11.2762 9.83528 11.2403C9.5621 11.2044 9.30841 11.0793 9.11358 10.8845C8.91875 10.6897 8.79367 10.436 8.75775 10.1628C8.72183 9.88961 8.77708 9.61221 8.91492 9.37364C9.05277 9.13506 9.2655 8.94866 9.52011 8.84334C9.77473 8.73803 10.057 8.71969 10.3231 8.79118L16.4323 2.68076C16.4903 2.62255 16.5593 2.57637 16.6352 2.54486C16.7112 2.51335 16.7926 2.49713 16.8748 2.49713C16.957 2.49713 17.0384 2.51335 17.1143 2.54486C17.1903 2.57637 17.2593 2.62255 17.3173 2.68076M18.7499 14.3753C18.7499 14.5411 18.6841 14.7001 18.5668 14.8173C18.4496 14.9345 18.2906 15.0004 18.1249 15.0004C17.9591 15.0004 17.8001 14.9345 17.6829 14.8173C17.5657 14.7001 17.4998 14.5411 17.4998 14.3753C17.4998 14.2095 17.5657 14.0506 17.6829 13.9333C17.8001 13.8161 17.9591 13.7503 18.1249 13.7503C18.2906 13.7503 18.4496 13.8161 18.5668 13.9333C18.6841 14.0506 18.7499 14.2095 18.7499 14.3753ZM18.1249 6.24976C18.2906 6.24976 18.4496 6.1839 18.5668 6.06668C18.6841 5.94947 18.7499 5.79048 18.7499 5.62471C18.7499 5.45894 18.6841 5.29996 18.5668 5.18274C18.4496 5.06552 18.2906 4.99967 18.1249 4.99967C17.9591 4.99967 17.8001 5.06552 17.6829 5.18274C17.5657 5.29996 17.4998 5.45894 17.4998 5.62471C17.4998 5.79048 17.5657 5.94947 17.6829 6.06668C17.8001 6.1839 17.9591 6.24976 18.1249 6.24976ZM20 11.5626C20 11.7284 19.9341 11.8874 19.8169 12.0046C19.6997 12.1218 19.5407 12.1877 19.375 12.1877C19.2092 12.1877 19.0502 12.1218 18.933 12.0046C18.8158 11.8874 18.7499 11.7284 18.7499 11.5626C18.7499 11.3969 18.8158 11.2379 18.933 11.1207C19.0502 11.0034 19.2092 10.9376 19.375 10.9376C19.5407 10.9376 19.6997 11.0034 19.8169 11.1207C19.9341 11.2379 20 11.3969 20 11.5626ZM19.375 9.06245C19.5407 9.06245 19.6997 8.9966 19.8169 8.87938C19.9341 8.76216 20 8.60318 20 8.43741C20 8.27164 19.9341 8.11265 19.8169 7.99543C19.6997 7.87822 19.5407 7.81236 19.375 7.81236C19.2092 7.81236 19.0502 7.87822 18.933 7.99543C18.8158 8.11265 18.7499 8.27164 18.7499 8.43741C18.7499 8.60318 18.8158 8.76216 18.933 8.87938C19.0502 8.9966 19.2092 9.06245 19.375 9.06245ZM16.2497 11.5626C16.2497 11.7284 16.1839 11.8874 16.0667 12.0046C15.9495 12.1218 15.7905 12.1877 15.6247 12.1877C15.4589 12.1877 15.2999 12.1218 15.1827 12.0046C15.0655 11.8874 14.9997 11.7284 14.9997 11.5626C14.9997 11.3969 15.0655 11.2379 15.1827 11.1207C15.2999 11.0034 15.4589 10.9376 15.6247 10.9376C15.7905 10.9376 15.9495 11.0034 16.0667 11.1207C16.1839 11.2379 16.2497 11.3969 16.2497 11.5626ZM15.6247 9.06245C15.7905 9.06245 15.9495 8.9966 16.0667 8.87938C16.1839 8.76216 16.2497 8.60318 16.2497 8.43741C16.2497 8.27164 16.1839 8.11265 16.0667 7.99543C15.9495 7.87822 15.7905 7.81236 15.6247 7.81236C15.4589 7.81236 15.2999 7.87822 15.1827 7.99543C15.0655 8.11265 14.9997 8.27164 14.9997 8.43741C14.9997 8.60318 15.0655 8.76216 15.1827 8.87938C15.2999 8.9966 15.4589 9.06245 15.6247 9.06245Z" fill="currentColor" />
  </svg>
);

const AISparkleIcon = () => (
  <svg width="22" height="22" viewBox="0 0 22 22" fill="none">
    <path d="M5.10449 10.7547C4.94556 10.6888 4.94555 10.4632 5.10449 10.3973L6.21289 9.93832C7.899 9.23907 9.23828 7.89887 9.9375 6.21274L10.3975 5.10434C10.4634 4.94542 10.689 4.9454 10.7549 5.10434L11.2139 6.21274C11.9131 7.89894 13.2533 9.23909 14.9395 9.93832L16.0479 10.3973C16.2068 10.4632 16.2068 10.6888 16.0479 10.7547L14.9395 11.2147C13.2533 11.9139 11.9131 13.2532 11.2139 14.9393L10.7549 16.0477C10.689 16.2066 10.4634 16.2066 10.3975 16.0477L9.9375 14.9393C9.23827 13.2534 7.89879 11.9139 6.21289 11.2147L5.10449 10.7547Z" stroke="#CCF54E" strokeWidth="0.931371" />
    <path opacity="0.2" d="M4.0127 11.885C3.04173 11.2827 3.04173 9.87005 4.0127 9.26779C4.11286 9.20569 4.31488 9.12065 4.80078 8.91916C5.12984 8.7827 5.30503 8.70953 5.4707 8.6301C6.85291 7.9673 7.96809 6.85314 8.63086 5.47092C8.71029 5.30526 8.78251 5.12999 8.91894 4.80099C9.12033 4.31537 9.20548 4.11317 9.26758 4.01291C9.86983 3.04194 11.2825 3.04194 11.8848 4.01291C11.9469 4.11314 12.032 4.31522 12.2334 4.80099C12.3698 5.13004 12.443 5.30525 12.5225 5.47092C13.1852 6.85295 14.2996 7.96734 15.6816 8.6301C15.8473 8.70953 16.0225 8.78271 16.3516 8.91916C16.8373 9.1206 17.0394 9.20569 17.1396 9.26779C18.1106 9.87005 18.1106 11.2827 17.1396 11.885C17.0394 11.9471 16.8372 12.0322 16.3516 12.2336C16.0226 12.37 15.8473 12.4423 15.6816 12.5217C14.2994 13.1845 13.1853 14.2996 12.5225 15.6819C12.443 15.8475 12.3699 16.0227 12.2334 16.3518C12.0319 16.8377 11.9469 17.0397 11.8848 17.1399C11.2825 18.1108 9.86984 18.1108 9.26758 17.1399C9.20548 17.0397 9.1204 16.8376 8.91895 16.3518C8.78251 16.0228 8.71028 15.8475 8.63086 15.6819C7.96806 14.2995 6.85305 13.1845 5.4707 12.5217C5.30506 12.4423 5.1298 12.37 4.80078 12.2336C4.31497 12.0322 4.11289 11.9471 4.0127 11.885Z" stroke="#CCF54E" strokeWidth="0.615969" />
  </svg>
);

const SatelliteIcon = () => (
  <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
    <rect width="20" height="20" rx="4" fill="#CCF54E" fillOpacity="0.05" />
    <path d="M12.8727 7.12749L10.7323 4.98706C10.6563 4.91103 10.5325 4.91068 10.4611 4.99113C9.87081 5.65683 9.60554 6.51001 9.66497 7.34548M9.66497 7.34548C9.7181 8.09249 10.0308 8.82534 10.6028 9.39737C11.144 9.93853 11.9499 10.2585 12.6547 10.3352C13.5279 10.4303 14.3258 10.1594 15.0098 9.53985C15.0895 9.46767 15.0892 9.34395 15.0131 9.26792L12.8727 7.12749M12.8727 7.12749L14.0401 5.96006M9.66497 7.34548L8.36938 8.64107M12.6547 10.3352L11.3591 11.6308M8.36938 8.64107L6.54186 6.81354C6.46583 6.73751 6.34256 6.73751 6.26654 6.81354L4.07433 9.00575C3.9983 9.08178 3.9983 9.20504 4.07433 9.28107L5.90185 11.1086M8.36938 8.64107L5.90185 11.1086M5.90185 11.1086L5.38313 11.6273C4.87628 12.1342 4.87628 12.9559 5.38313 13.4628L6.5374 14.6171C7.04426 15.1239 7.86603 15.1239 8.37289 14.6171L8.89161 14.0984M11.3591 11.6308L8.89161 14.0984M11.3591 11.6308L13.1867 13.4583C13.2627 13.5344 13.2627 13.6576 13.1867 13.7337L10.9945 15.9259C10.9184 16.0019 10.7952 16.0019 10.7191 15.9259L8.89161 14.0984" stroke="#F0F0F0" strokeWidth="0.75" strokeLinecap="round" />
  </svg>
);

const PointIcon = () => (
  <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
    <rect width="20" height="20" rx="4" fill="#CCF54E" fillOpacity="0.05" />
    <circle cx="10" cy="10" r="5.87353" stroke="#F0F0F0" strokeWidth="0.752941" />
    <circle cx="10" cy="10" r="1.25" fill="#F0F0F0" />
  </svg>
);

const PolygonIcon = () => (
  <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
    <rect width="20" height="20" rx="4" fill="#CCF54E" fillOpacity="0.05" />
    <path d="M6.94691 7.13599C6.82379 6.54627 6.30097 6.10329 5.67468 6.10329C4.95689 6.10329 4.375 6.68517 4.375 7.40297C4.375 8.12076 4.95689 8.70265 5.67468 8.70265C6.39247 8.70265 6.97436 8.12076 6.97436 7.40297C6.97436 7.31146 6.9649 7.22216 6.94691 7.13599ZM5.67468 8.70265V13.0242M11.3253 14.324C11.3253 15.0418 11.9072 15.6237 12.625 15.6237C13.3428 15.6237 13.9247 15.0418 13.9247 14.324C13.9247 13.6852 13.4639 13.1541 12.8566 13.0449M11.3253 14.324C11.3253 13.6062 11.9072 13.0243 12.625 13.0243C12.704 13.0243 12.7814 13.0313 12.8566 13.0449M11.3253 14.324L6.97436 14.3239M6.97436 14.3239C6.97436 15.0417 6.39247 15.6236 5.67468 15.6236C4.95689 15.6236 4.375 15.0417 4.375 14.3239C4.375 13.6061 4.95689 13.0242 5.67468 13.0242M6.97436 14.3239C6.97436 13.6061 6.39247 13.0242 5.67468 13.0242M12.8566 13.0449L14.0654 6.95007M13.0468 5.91106C13.1418 6.43227 13.5477 6.84499 14.0654 6.95007C14.1494 6.96712 14.2363 6.97607 14.3253 6.97607C15.0431 6.97607 15.625 6.39418 15.625 5.67639C15.625 4.9586 15.0431 4.37671 14.3253 4.37671C13.6075 4.37671 13.0256 4.9586 13.0256 5.67639C13.0256 5.75651 13.0329 5.83495 13.0468 5.91106ZM6.94691 7.13599L13.0468 5.91106" stroke="#F0F0F0" strokeWidth="0.84375" />
  </svg>
);

const GroundStationIcon = () => (
  <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
    <rect width="20" height="20" rx="4" fill="#CCF54E" fillOpacity="0.05" />
    <path d="M12.1641 3.43808C12.7158 3.39262 13.265 3.55212 13.7065 3.88607C14.1481 4.22003 14.4511 4.70502 14.5575 5.24831M12.0193 4.37577C12.4088 4.32568 12.8025 4.43033 13.1157 4.66725C13.429 4.90416 13.6369 5.25446 13.6947 5.64293" stroke="#F0F0F0" strokeWidth="0.62321" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M7.63978 5.15006L7.98565 5.41164M10.8668 7.59068L7.98565 5.41164M10.8668 7.59068L13.7479 9.76971M10.8668 7.59068L12.2426 5.77143M7.98565 5.41164C6.59358 7.25234 6.9199 9.10501 8.70212 10.4529C10.4843 11.8008 12.3558 11.6104 13.7479 9.76971M14.0938 10.0313L13.7479 9.76971" stroke="#F0F0F0" strokeWidth="0.779012" strokeLinecap="round" />
    <path d="M12.2949 14.938L11.8613 14.938M11.8613 14.938L8.78604 14.938L5.17373 14.938M11.8613 14.938L6.71148 10.5911C6.10372 10.0781 5.17372 10.5101 5.17372 11.3055L5.17373 14.938M4.74008 14.938L5.17373 14.938" stroke="#F0F0F0" strokeWidth="0.77901" strokeLinecap="round" />
    <ellipse cx="6.43611" cy="12.0567" rx="0.496657" ry="0.49667" fill="#F0F0F0" />
  </svg>
);

const SearchIcon = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
    <path d="M7 12a5 5 0 100-10 5 5 0 000 10zM14 14l-3.5-3.5" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const FilterIcon = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
    <path d="M2 3h12M4 7h8M6 11h4" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const MaximizeIcon = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
    <path d="M1.5 5.5v-4h4M14.5 5.5v-4h-4M1.5 10.5v4h4M14.5 10.5v4h-4" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const SatIcon = () => (
  <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
    <path d="M10.298 5.70199L8.58563 3.98965C8.52481 3.92883 8.42578 3.92854 8.36871 3.9929C7.89645 4.52547 7.68424 5.20801 7.73178 5.87638M7.73178 5.87638C7.77429 6.47399 8.02446 7.06027 8.48208 7.51789C8.91501 7.95083 9.55969 8.20678 10.1236 8.26819C10.8221 8.34426 11.4605 8.12754 12.0077 7.63188C12.0714 7.57414 12.0711 7.47516 12.0103 7.41433L10.298 5.70199M10.298 5.70199L11.2319 4.76805M7.73178 5.87638L6.69531 6.91285M10.1236 8.26819L9.08712 9.30466M6.69531 6.91285L5.23329 5.45083C5.17247 5.39001 5.07386 5.39001 5.01303 5.45083L3.25927 7.2046C3.19844 7.26542 3.19845 7.36404 3.25927 7.42486L4.72129 8.88688M6.69531 6.91285L4.72129 8.88688M4.72129 8.88688L4.30631 9.30185C3.90083 9.70734 3.90083 10.3648 4.30631 10.7702L5.22973 11.6937C5.63521 12.0991 6.29263 12.0991 6.69812 11.6937L7.11309 11.2787M9.08712 9.30466L7.11309 11.2787M9.08712 9.30466L10.5491 10.7667C10.61 10.8275 10.61 10.9261 10.5491 10.9869L8.79537 12.7407C8.73455 12.8015 8.63593 12.8015 8.57511 12.7407L7.11309 11.2787" stroke="currentColor" strokeWidth="0.8" strokeLinecap="round" />
  </svg>
);

const GeoIcon = () => (
  <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
    <path d="M5.55753 5.70877C5.45903 5.23699 5.04077 4.8826 4.53974 4.8826C3.96551 4.8826 3.5 5.34811 3.5 5.92235C3.5 6.49658 3.96551 6.96209 4.53974 6.96209C5.11398 6.96209 5.57949 6.49658 5.57949 5.92235C5.57949 5.84914 5.57192 5.7777 5.55753 5.70877ZM4.53974 6.96209V10.4194M9.06028 11.4592C9.06028 12.0334 9.52579 12.4989 10.1 12.4989C10.6743 12.4989 11.1398 12.0334 11.1398 11.4592C11.1398 10.9481 10.7711 10.5232 10.2852 10.4359M9.06028 11.4592C9.06028 10.8849 9.52579 10.4194 10.1 10.4194C10.1632 10.4194 10.2251 10.4251 10.2852 10.4359M9.06028 11.4592L5.57949 11.4591M5.57949 11.4591C5.57949 12.0333 5.11398 12.4988 4.53974 12.4988C3.96551 12.4988 3.5 12.0333 3.5 11.4591C3.5 10.8849 3.96551 10.4194 4.53974 10.4194M5.57949 11.4591C5.57949 10.8849 5.11398 10.4194 4.53974 10.4194M10.2852 10.4359L11.2523 5.56003M10.4374 4.72883C10.5135 5.14579 10.8382 5.47596 11.2523 5.56003C11.3195 5.57367 11.389 5.58083 11.4603 5.58083C12.0345 5.58083 12.5 5.11532 12.5 4.54109C12.5 3.96685 12.0345 3.50134 11.4603 3.50134C10.886 3.50134 10.4205 3.96685 10.4205 4.54109C10.4205 4.60519 10.4263 4.66793 10.4374 4.72883ZM5.55753 5.70877L10.4374 4.72883" stroke="currentColor" strokeWidth="0.8" />
  </svg>
);

const GridIcon = () => (
  <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
    <rect x="2.5" y="2.5" width="7" height="7" rx="1.5" stroke="currentColor" strokeWidth="0.8" />
    <circle cx="6" cy="6" r="1" fill="currentColor" />
  </svg>
);

interface FigmaCustomSelectProps {
  label?: string;
  value: string;
  options: { value: string; label: string }[];
  onChange: (val: string) => void;
  disabled?: boolean;
}

export const FigmaCustomSelect: React.FC<FigmaCustomSelectProps> = ({ label, value, options, onChange, disabled }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    if (open) document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  const selectedOption = options.find(o => o.value === value);
  const displayLabel = selectedOption?.label ?? value;

  return (
    <div className={styles.filterGroup}>
      {label ? <span className={styles.filterGroupLabel}>{label}</span> : null}
      <div className={styles.customSelectWrap} ref={ref}>
        <button
          type="button"
          disabled={disabled}
          className={`${styles.customSelectTrigger} ${disabled ? styles.customSelectDisabled : ''}`}
          onClick={() => !disabled && setOpen(o => !o)}
        >
          <span className={styles.customSelectValue}>{displayLabel}</span>
          <span className={`${styles.customSelectCaret} ${open ? styles.customSelectCaretOpen : ''}`}>
            <svg width="10" height="10" viewBox="0 0 12 12" fill="none">
              <path d="M2.5 4.5L6 8L9.5 4.5" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </span>
        </button>
        {open && !disabled && (
          <div className={styles.customSelectDropdown}>
            {options.map(opt => (
              <div
                key={opt.value}
                className={`${styles.customSelectOption} ${opt.value === value ? styles.customSelectOptionActive : ''}`}
                onClick={() => {
                  onChange(opt.value);
                  setOpen(false);
                }}
              >
                {opt.label}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

const formatReportDate = (value: AccessAnalysisReport['createdAt']) => {
  if (value == null || value === '') return '';
  const date = typeof value === 'number'
    ? new Date(value > 10_000_000_000 ? value : value * 1000)
    : new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  const datePart = date.toLocaleDateString('en-GB').replace(/\//g, '.');
  const timePart = date.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', hour12: false });
  return `${datePart} at ${timePart}`;
};

const getReportName = (report: Pick<AccessAnalysisReport, 'name'>, index = 0) =>
  report.name || `REPORT_${String(index + 1).padStart(2, '0')}`;

const isComplete = (report: AccessAnalysisReport) => String(report.status).toLowerCase() === 'completed';
const isFailed = (report: AccessAnalysisReport) => String(report.status).toLowerCase() === 'failed';

const getRequestInput = (report: any) => {
  const raw = report?.raw ?? {};
  return raw.requestInput ?? raw.request_input ?? raw.input ?? raw.request ?? raw.requestBody ?? raw.request_body ?? raw;
};

const formatStartEnd = (report: any) => {
  const inp = getRequestInput(report);
  const startVal = inp?.startTime ?? inp?.start_time;
  if (!startVal) return '';
  const dur = inp?.duration ?? inp?.durationHrs ?? inp?.durationHours ?? 24;
  const dStart = new Date(startVal > 10_000_000_000 ? startVal : startVal * 1000);
  const dEnd = new Date(dStart.getTime() + dur * 3600 * 1000);
  const fmt = (d: Date) => d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false, timeZone: 'UTC' }) + ' ' + d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', timeZone: 'UTC' });
  return `${fmt(dStart)} - ${fmt(dEnd)}`;
};

const getReportInput = (report: AccessAnalysisReport | null) => {
  if (!report) return {};
  return getRequestInput(report);
};

const getSourceEntries = (input: any) => {
  const groups = input?.groups ?? input?.groupIds ?? input?.group_ids ?? [];
  if (!Array.isArray(groups)) return [];
  return groups.map((item: any) => {
    if (typeof item === 'string') return { id: item, type: 'Single' };
    return {
      id: String(item?.objectId ?? item?.object_id ?? item?.id ?? ''),
      type: String(item?.objectType ?? item?.object_type ?? 'Single'),
    };
  }).filter((item: { id: string }) => item.id);
};

const getIdList = (...values: any[]) => {
  const value = values.find(item => Array.isArray(item));
  if (!value) return [];
  return value.map((item: any) => String(typeof item === 'string' ? item : item?.id ?? item?.objectId ?? item?.aoiId ?? '')).filter(Boolean);
};

const getList = (payload: any): any[] => {
  if (!payload) return [];
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload.items)) return payload.items;
  if (Array.isArray(payload.content)) return payload.content;
  if (Array.isArray(payload.events)) return payload.events;
  if (Array.isArray(payload.passes)) return payload.passes;
  if (Array.isArray(payload.data)) return payload.data;
  if (payload.data && typeof payload.data === 'object') {
    return getList(payload.data);
  }
  return [];
};

const getPageMeta = (payload: any) => {
  let inner = payload;
  if (payload && payload.data && typeof payload.data === 'object' && !Array.isArray(payload.data)) {
    inner = payload.data;
  }
  const items = getList(inner);
  const page = Number(inner?.page ?? 0);
  const size = Number(inner?.size ?? items.length);
  const totalCount = Number(inner?.totalCount ?? inner?.total_count ?? items.length);
  return {
    page: Number.isFinite(page) ? page : 0,
    size: Number.isFinite(size) && size > 0 ? size : items.length,
    totalCount: Number.isFinite(totalCount) ? totalCount : items.length,
  };
};

const getListKey = (payload: any): string => {
  let inner = payload;
  if (payload && payload.data && typeof payload.data === 'object' && !Array.isArray(payload.data)) {
    inner = payload.data;
  }
  if (Array.isArray(inner?.items)) return 'items';
  if (Array.isArray(inner?.content)) return 'content';
  if (Array.isArray(inner?.events)) return 'events';
  if (Array.isArray(inner?.passes)) return 'passes';
  if (Array.isArray(inner?.data)) return 'data';
  return 'items';
};

const mergeEventPages = (prev: any, next: any) => {
  if (!prev) return next;
  const prevItems = getList(prev);
  const nextItems = getList(next);
  const mergedItems = [...prevItems, ...nextItems];
  if (Array.isArray(next)) return mergedItems;
  const key = getListKey(next);
  return {
    ...prev,
    ...next,
    [key]: mergedItems,
  };
};

const getTopPasses = (payload: any): any[] => {
  const list = getList(payload);
  if (list.length > 0) return list;
  if (!payload || typeof payload !== 'object') return [];
  return Object.entries(payload).flatMap(([category, value]) => (
    Array.isArray(value)
      ? value.map(item => ({ ...item, category }))
      : []
  ));
};

const TOP_PASS_CATEGORY_LABELS: Record<string, string> = {
  sat_to_sat: 'SAT-SAT',
  sat_to_spot_target: 'SAT-TARGET',
  sat_to_geo_feature: 'SAT-TARGET',
  sat_to_geofence: 'SAT-AREA GEOFEATURE',
  sat_to_ground_station: 'SAT-GROUND STATION',
};

const TOP_PASS_CATEGORY_ORDER = [
  'sat_to_sat',
  'sat_to_spot_target',
  'sat_to_geo_feature',
  'sat_to_geofence',
  'sat_to_ground_station',
];

const getPassCategory = (item: any) => String(item?.category ?? item?.pairType ?? 'other');

const getTopPassGroups = (payload: any) => {
  const passes = getTopPasses(payload);
  const grouped = passes.reduce((acc: Record<string, any[]>, item: any) => {
    const category = getPassCategory(item);
    acc[category] = acc[category] ?? [];
    acc[category].push(item);
    return acc;
  }, {});
  const orderedKeys = [
    ...TOP_PASS_CATEGORY_ORDER.filter(key => grouped[key]?.length),
    ...Object.keys(grouped).filter(key => !TOP_PASS_CATEGORY_ORDER.includes(key)),
  ];
  return orderedKeys.map(key => ({
    key,
    label: TOP_PASS_CATEGORY_LABELS[key] ?? formatPassCategory(key),
    items: grouped[key],
  }));
};

const pick = (source: any, keys: string[], fallback: any = '-') => {
  for (const key of keys) {
    const value = source?.[key];
    if (value !== undefined && value !== null && value !== '') return value;
  }
  return fallback;
};

const formatTime = (value: any) => {
  if (value == null || value === '') return '-';
  const date = typeof value === 'number'
    ? new Date(value > 10_000_000_000 ? value : value * 1000)
    : new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return date.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false, timeZone: 'UTC' });
};

const formatDuration = (value: any) => {
  const seconds = Number(value);
  if (!Number.isFinite(seconds)) return value == null || value === '' ? '-' : String(value);
  const hours = Math.floor(seconds / 3600);
  const mins = Math.floor((seconds % 3600) / 60);
  const secs = Math.round(seconds % 60);
  if (hours > 0) return `${hours}h ${mins}m ${secs}s`;
  if (mins <= 0) return `${secs}s`;
  return `${mins}m ${secs}s`;
};

const formatSummaryValue = (value: any) => {
  if (value == null || value === '') return '-';
  if (typeof value === 'object') {
    const accessed = value.accessed ?? value.count ?? value.value;
    const total = value.total;
    if (accessed != null && total != null) return `${accessed}/${total}`;
    if (accessed != null) return String(accessed);
    return '-';
  }
  if (typeof value === 'number' && Number.isFinite(value) && !Number.isInteger(value)) {
    return String(Number(value.toFixed(2)));
  }
  return String(value);
};

const formatMetricValue = (label: string, value: any) => {
  if (value == null || value === '' || value === '-') return '-';
  const upperLabel = label.toUpperCase();
  if (
    ['TOTAL ACCESS TIME', 'AVG DURATION', 'MAX DURATION', 'MIN DURATION'].includes(upperLabel) ||
    upperLabel.includes('TIME') ||
    upperLabel.includes('DURATION')
  ) {
    return formatDuration(value);
  }
  if (upperLabel === 'PASSES PER DAY') {
    return `${formatSummaryValue(value)} / day`;
  }
  return formatSummaryValue(value);
};

const formatElevation = (value: any) => {
  const elevation = Number(value);
  if (!Number.isFinite(elevation)) return value == null || value === '' ? '-' : String(value);
  return `${Math.round(elevation)}°`;
};

const formatDegrees = (value: any) => {
  const degrees = Number(value);
  if (!Number.isFinite(degrees)) return value == null || value === '' ? '-' : String(value);
  return `${Number(degrees.toFixed(2))} deg`;
};

const formatPassCategory = (value: any) => String(value ?? '')
  .replace(/^sat_to_/, '')
  .replace(/_/g, ' ')
  .toUpperCase();

const formatObjectName = (value: any, fallback: string) => {
  if (value == null || value === '') return fallback;
  const label = String(value);
  return /^[0-9a-f]{8}-[0-9a-f-]{27}$/i.test(label) ? `${fallback} ${label.slice(0, 8)}` : label;
};

const getChatAnswer = (payload: any) => {
  const data = payload?.data ?? payload;
  const answer = data?.answer ?? data?.response ?? data?.message ?? data?.content ?? data?.text ?? data?.result;
  if (typeof answer === 'string') return answer;
  if (answer != null) return JSON.stringify(answer);
  return 'No response received.';
};

const getCounterpartyFallback = (kind: any, category: any) => {
  const type = String(kind ?? category ?? '').toLowerCase();
  if (type.includes('ground_station')) return 'GS';
  if (type.includes('geofence')) return 'Area';
  if (type.includes('geo_feature_spot') || type.includes('spot') || type.includes('target')) return 'Target';
  if (type.includes('satellite') || type.includes('sat_to_sat')) return 'Sat';
  return 'Object';
};

const resolveEntityName = (
  value: any,
  type: keyof EntityNameMaps,
  maps: EntityNameMaps,
  fallbackPrefix: string,
) => {
  if (value == null || value === '') return fallbackPrefix;
  const id = String(value);
  return maps[type].get(id) ?? formatObjectName(id, fallbackPrefix);
};

const resolveCounterpartyName = (item: any, maps: EntityNameMaps) => {
  const kind = String(item.counterpartyKind ?? item.category ?? item.pairType ?? '').toLowerCase();
  const id = pick(item, ['counterpartyId', 'targetId', 'groundStationId'], '');
  if (kind.includes('ground_station')) return resolveEntityName(id, 'groundStations', maps, 'GS');
  if (kind.includes('geo_feature') || kind.includes('spot') || kind.includes('geofence') || kind.includes('target')) {
    return resolveEntityName(id, 'geoFeatures', maps, getCounterpartyFallback(item.counterpartyKind, item.category ?? item.pairType));
  }
  if (kind.includes('satellite') || kind.includes('sat_to_sat')) return resolveEntityName(id, 'satellites', maps, 'Sat');
  return resolveEntityName(id, 'geoFeatures', maps, getCounterpartyFallback(item.counterpartyKind, item.category ?? item.pairType));
};

const formatPassPairName = (item: any, index: number, maps: EntityNameMaps) => {
  const explicit = pick(item, ['pairName', 'name', 'target'], '');
  if (explicit) return explicit;
  const satelliteName = pick(item, ['satelliteName', 'satName'], '');
  const counterpartyName = pick(item, ['counterpartyName', 'groundStationName', 'targetName'], '');
  const satellite = satelliteName || resolveEntityName(pick(item, ['satelliteId', 'satId'], ''), 'satellites', maps, 'Sat');
  const counterparty = counterpartyName || resolveCounterpartyName(item, maps);
  if (satellite !== 'Sat' && counterparty !== 'Object') return `${satellite}-${counterparty}`;
  return pick(item, ['pairKey'], `Pass ${index + 1}`);
};

const buildEntityNameMaps = (
  satellites: Array<Satellite | SatGroup>,
  groundStations: GroundStation[],
  geoFeatures: GeoFeature[],
): EntityNameMaps => {
  const satelliteNames = new Map<string, string>();
  satellites.forEach((item) => {
    if ((item as SatGroup).type === 'group') {
      const group = item as SatGroup;
      satelliteNames.set(group.id, group.name);
      group.children.forEach((sat) => {
        satelliteNames.set(sat.id, sat.name);
        if (sat.simulationGroupId) satelliteNames.set(sat.simulationGroupId, sat.name);
      });
    } else {
      const sat = item as Satellite;
      satelliteNames.set(sat.id, sat.name);
      if (sat.simulationGroupId) satelliteNames.set(sat.simulationGroupId, sat.name);
    }
  });

  return {
    satellites: satelliteNames,
    groundStations: new Map(groundStations.map(item => [item.id, item.name])),
    geoFeatures: new Map(geoFeatures.map(item => [item.id, item.name])),
  };
};

const findPlotSourceItem = (satellites: Array<Satellite | SatGroup>, id: string, sourceType: string) => {
  const normalizedType = sourceType.toLowerCase();
  for (const item of satellites) {
    if ((item as SatGroup).type === 'group') {
      const group = item as SatGroup;
      const simulationGroupMatch = group.children.some(sat => sat.simulationGroupId === id);
      if (group.id === id || (normalizedType.includes('group') && simulationGroupMatch)) return group;
      const child = group.children.find(sat => sat.id === id || sat.simulationGroupId === id);
      if (child) return child;
    } else {
      const sat = item as Satellite;
      if (sat.id === id || sat.simulationGroupId === id) return sat;
    }
  }
  return null;
};

const buildPlotSourceOptions = (
  report: AccessAnalysisReport | null,
  satellites: Array<Satellite | SatGroup>,
): PlotSourceOption[] => {
  const input = getReportInput(report);
  const sources = getSourceEntries(input);
  const treeOptions = satellites.map((item) => {
    if ((item as SatGroup).type === 'group') {
      const group = item as SatGroup;
      return {
        id: group.id,
        label: group.name,
        type: 'group' as const,
        satelliteIds: group.children.map(sat => sat.id).filter(Boolean),
      };
    }
    const sat = item as Satellite;
    return {
      id: sat.id,
      label: sat.name,
      type: 'single' as const,
      satelliteIds: [sat.id].filter(Boolean),
    };
  });
  const sourceOptions = sources.map((source) => {
    const item = findPlotSourceItem(satellites, source.id, source.type);
    if (item && (item as SatGroup).type === 'group') {
      const group = item as SatGroup;
      return {
        id: group.id,
        label: group.name,
        type: 'group' as const,
        satelliteIds: group.children.map(sat => sat.id).filter(Boolean),
      };
    }
    const sat = item as Satellite | null;
    const fallbackIsGroup = source.type.toLowerCase() === 'group';
    return {
      id: source.id,
      label: sat?.name ?? source.id,
      type: fallbackIsGroup ? 'group' as const : 'single' as const,
      satelliteIds: fallbackIsGroup ? [] : [sat?.id ?? source.id].filter(Boolean),
    };
  }).filter(option => option.satelliteIds.length > 0);

  if (sourceOptions.length > 0) {
    return Array.from(new Map(sourceOptions.map(option => [option.id, option])).values());
  }

  return Array.from(new Map(treeOptions.map(option => [option.id, option])).values());
};

const buildPlotGroundStationOptions = (
  report: AccessAnalysisReport | null,
  maps: EntityNameMaps,
): PlotGroundStationOption[] => {
  const input = getReportInput(report);
  const gsIds = getIdList(input?.groundstationIds, input?.groundStationIds, input?.ground_station_ids);
  if (gsIds.length > 0) {
    return gsIds.map(id => ({
      id,
      label: resolveEntityName(id, 'groundStations', maps, 'GS'),
    }));
  }
  const gfIds = getIdList(input?.targetGeofeatureIds, input?.targetGeofeatures, input?.target_geofeature_ids, input?.geofeatureIds, input?.geoFeatureIds);
  return gfIds.map(id => ({
    id,
    label: resolveEntityName(id, 'geoFeatures', maps, 'Target'),
  }));
};

const normalizePlotItems = (data: any) => {
  if (!data) return [];
  const records = Array.isArray(data) ? data : [data];
  return records.flatMap((record) => {
    const nested = getList(record?.data);
    const basePlots = nested.length > 0 ? nested : getList(record);
    const plots = basePlots.length > 0 ? basePlots : [record?.data ?? record].filter(Boolean);
    return plots.map((plot: any, index: number) => ({
      ...plot,
      id: plot?.id ?? plot?.pairKey ?? `${record?.satelliteId ?? 'plot'}-${index}`,
      satelliteName: plot?.satelliteName ?? record?.satelliteName,
      groundStationName: plot?.groundStationName ?? record?.groundStationName,
      groundStationId: plot?.groundStationId ?? record?.groundStationId,
      satelliteId: plot?.satelliteId ?? record?.satelliteId,
    }));
  });
};

const getAerPoints = (plot: any): AerPoint[] => {
  const source = plot?.aerPoints ?? plot?.aer_points ?? plot?.points ?? plot?.samples ?? plot?.aer;
  if (!Array.isArray(source)) return [];
  return source.map((point: any) => ({
    timeUtc: point?.timeUtc ?? point?.time_utc ?? point?.time,
    azimuthDeg: Number(point?.azimuthDeg ?? point?.azimuth ?? point?.az),
    elevationDeg: Number(point?.elevationDeg ?? point?.elevation ?? point?.el),
    rangeKm: point?.rangeKm == null ? undefined : Number(point.rangeKm),
  })).filter(point => Number.isFinite(point.azimuthDeg) && Number.isFinite(point.elevationDeg));
};

const hasPlotData = (plot: any) => getAerPoints(plot).length > 0;

const getPolarPoint = (point: AerPoint) => {
  const radius = 95;
  const center = 110;
  const normalizedElevation = Math.max(0, Math.min(90, point.elevationDeg));
  const radialDistance = radius * (1 - normalizedElevation / 90);
  const rad = ((point.azimuthDeg - 90) * Math.PI) / 180;
  return {
    x: center + Math.cos(rad) * radialDistance,
    y: center + Math.sin(rad) * radialDistance,
  };
};

const getPolarPath = (points: AerPoint[]) => points.map((point, index) => {
  const { x, y } = getPolarPoint(point);
  return `${index === 0 ? 'M' : 'L'}${Number(x.toFixed(2))} ${Number(y.toFixed(2))}`;
}).join(' ');

const formatPlotDegreeValue = (value: number | null) => (
  value == null || !Number.isFinite(value) ? '-' : `${Number(value.toFixed(2))}°`
);

const formatPolarPointTooltip = (point: AerPoint, index: number) => {
  const parts = [
    `Point ${index + 1}`,
    `Azimuth: ${formatPlotDegreeValue(point.azimuthDeg)}`,
    `Elevation: ${formatPlotDegreeValue(point.elevationDeg)}`,
  ];
  if (point.rangeKm != null && Number.isFinite(point.rangeKm)) {
    parts.push(`Range: ${Number(point.rangeKm.toFixed(2))} km`);
  }
  if (point.timeUtc) parts.push(`Time: ${String(point.timeUtc)}`);
  return parts.join('\n');
};

const emptyReportHighlights = (): ReportHoverHighlights => ({
  satelliteIds: [],
  groupIds: [],
  geoFeatureIds: [],
  groundStationIds: [],
});

const getCoverageSatHighlights = (report: CoverageAnalysisReport | null, satellites: Array<Satellite | SatGroup>): ReportHoverHighlights => {
  if (!report) return emptyReportHighlights();
  const input = report.raw?.requestInput ?? {};
  const sourceEntries = getSourceEntries(input);
  const sourceHighlights = emptyReportHighlights();
  sourceEntries.forEach((source) => {
    const item = findPlotSourceItem(satellites, source.id, source.type);
    if (item && (item as SatGroup).type === 'group') {
      const group = item as SatGroup;
      sourceHighlights.groupIds.push(group.id);
      sourceHighlights.satelliteIds.push(...group.children.map(sat => sat.id).filter(Boolean));
      return;
    }
    const sat = item as Satellite | null;
    if (sat?.id) {
      sourceHighlights.satelliteIds.push(sat.id);
      if (sat.simulationGroupId && source.type.toLowerCase().includes('group')) sourceHighlights.groupIds.push(sat.simulationGroupId);
      return;
    }
    if (source.type.toLowerCase().includes('group')) sourceHighlights.groupIds.push(source.id);
    else sourceHighlights.satelliteIds.push(source.id);
  });
  return {
    ...emptyReportHighlights(),
    satelliteIds: Array.from(new Set(sourceHighlights.satelliteIds)),
    groupIds: Array.from(new Set(sourceHighlights.groupIds)),
  };
};

const getCoverageAoiHighlights = (report: CoverageAnalysisReport | null): ReportHoverHighlights => {
  if (!report) return emptyReportHighlights();
  const input = report.raw?.requestInput ?? {};
  const aoiIds = getIdList(input?.aoiIds, input?.aoi_ids, input?.geoFeatureIds, input?.geo_feature_ids, input?.aoiobjects);
  return {
    ...emptyReportHighlights(),
    geoFeatureIds: aoiIds,
  };
};

const buildInputCards = (
  input: any,
  geoFeatures: GeoFeature[],
  satellites: Array<Satellite | SatGroup>,
): InputSummaryCard[] => {
  const sourceEntries = getSourceEntries(input);
  const gsIds = new Set(getIdList(input?.groundstationIds, input?.groundStationIds, input?.ground_station_ids));
  const aoiIds = new Set(getIdList(input?.aoiIds, input?.aoi_ids, input?.geoFeatureIds, input?.geo_feature_ids));
  const selectedGeoFeatures = geoFeatures.filter(feature => aoiIds.has(feature.id));
  const pointGeoFeatures = selectedGeoFeatures.filter(feature => feature.kind !== 'polygon');
  const polygonGeoFeatures = selectedGeoFeatures.filter(feature => feature.kind === 'polygon');
  const sourceHighlights = emptyReportHighlights();
  sourceEntries.forEach((source) => {
    const item = findPlotSourceItem(satellites, source.id, source.type);
    if (item && (item as SatGroup).type === 'group') {
      const group = item as SatGroup;
      sourceHighlights.groupIds.push(group.id);
      sourceHighlights.satelliteIds.push(...group.children.map(sat => sat.id).filter(Boolean));
      return;
    }
    const sat = item as Satellite | null;
    if (sat?.id) {
      sourceHighlights.satelliteIds.push(sat.id);
      if (sat.simulationGroupId && source.type.toLowerCase().includes('group')) sourceHighlights.groupIds.push(sat.simulationGroupId);
      return;
    }
    if (source.type.toLowerCase().includes('group')) sourceHighlights.groupIds.push(source.id);
    else sourceHighlights.satelliteIds.push(source.id);
  });
  return [
    {
      key: 'sources',
      count: sourceEntries.length,
      icon: <SatelliteIcon />,
      highlights: {
        ...sourceHighlights,
        satelliteIds: Array.from(new Set(sourceHighlights.satelliteIds)),
        groupIds: Array.from(new Set(sourceHighlights.groupIds)),
      },
    },
    {
      key: 'points',
      count: pointGeoFeatures.length,
      icon: <PointIcon />,
      highlights: { ...emptyReportHighlights(), geoFeatureIds: pointGeoFeatures.map(feature => feature.id) },
    },
    {
      key: 'polygons',
      count: polygonGeoFeatures.length,
      icon: <PolygonIcon />,
      highlights: { ...emptyReportHighlights(), geoFeatureIds: polygonGeoFeatures.map(feature => feature.id) },
    },
    {
      key: 'groundStations',
      count: gsIds.size,
      icon: <GroundStationIcon />,
      highlights: { ...emptyReportHighlights(), groundStationIds: Array.from(gsIds) },
    },
  ];
};

const toEpoch = (val: any): number | null => {
  if (val == null) return null;
  if (typeof val === 'number') return val;
  const num = Number(val);
  if (Number.isFinite(num)) return num;
  const parsed = new Date(val).getTime();
  return Number.isNaN(parsed) ? null : Math.floor(parsed / 1000);
};

const SummaryContent: React.FC<{ data: any; loading: boolean; entityNames: EntityNameMaps; currentEpoch: number | null }> = ({ data, loading, entityNames, currentEpoch }) => {
  const [collapsedGroups, setCollapsedGroups] = useState<Record<string, boolean>>({});
  if (loading) return <div className={styles.tabMessage}>Loading summary...</div>;
  const summary = data ?? {};
  const overview = summary.overview ?? summary.metrics ?? summary;
  const topPassPayload = summary.topPasses ?? summary.top_passes ?? summary.passesByType ?? summary.passes;
  const topPassGroups = getTopPassGroups(topPassPayload);
  const topPassCount = topPassGroups.reduce((count, group) => count + group.items.length, 0);
  const mostActiveSatId = pick(overview, ['mostActiveSat', 'most_active_sat', 'mostActiveSatellite'], '');
  const mostUtilizedGsId = pick(overview, ['mostUtilizedGs', 'most_utilized_gs', 'mostUtilizedGroundStation'], '');
  const isFiltered = (summary.applied_filters && summary.applied_filters.length > 0) || summary.filter_type === 'CUSTOM_FILTER';

  const metricCards = isFiltered && overview && typeof overview === 'object'
    ? Object.entries(overview)
      .filter(([key, val]) => typeof val !== 'object' || val === null)
      .map(([key, val]) => {
        const formattedKey = key
          .replace(/_/g, ' ')
          .replace(/([A-Z])/g, ' $1')
          .trim()
          .toUpperCase();
        return [formattedKey, val];
      })
    : [
      ['TOTAL ACCESSES', pick(overview, ['totalAccesses', 'total_accesses', 'totalPasses', 'total_passes'], 0)],
      ['TOTAL ACCESS TIME', pick(overview, ['totalAccessTime', 'total_access_time', 'totalAccessTimeMin'], '-')],
      ['AVG DURATION', pick(overview, ['avgDuration', 'avg_duration', 'averageDuration'], '-')],
      ['MAX DURATION', pick(overview, ['maxDuration', 'max_duration'], '-')],
      ['MIN DURATION', pick(overview, ['minDuration', 'min_duration'], '-')],
      ['SAT PARTICIPATING', pick(overview, ['satParticipating', 'sat_participating', 'satellitesParticipating'], '-')],
      ['TARGET ACCESSED', pick(overview, ['targetAccessed', 'target_accessed', 'targetsAccessed'], '-')],
      ['GS ACCESSED', pick(overview, ['gsAccessed', 'gs_accessed', 'groundStationsAccessed'], '-')],
      ['MOST ACTIVE SAT', mostActiveSatId ? resolveEntityName(mostActiveSatId, 'satellites', entityNames, 'Sat') : '-'],
      ['MOST UTILIZED GS', mostUtilizedGsId ? resolveEntityName(mostUtilizedGsId, 'groundStations', entityNames, 'GS') : '-'],
      ['PASSES PER DAY', pick(overview, ['passesPerDay', 'passes_per_day', 'ground_contact_rate_per_day'], '-')],
      ['MISSION EFFICIENCY', pick(overview, ['missionEfficiency', 'mission_efficiency'], '-')],
    ];

  return (
    <div className={styles.summaryTab}>
      <div className={styles.figmaOverviewBlock}>
        <span className={styles.figmaOverviewTitle}>Overview</span>
        <div className={styles.figmaKpiRow}>
          {metricCards.map(([label, value]) => (
            <div key={label} className={styles.figmaKpiCard}>
              <span className={styles.figmaKpiLabel}>{label}</span>
              <div className={styles.figmaKpiValueSlot}>
                <span className={styles.figmaKpiValue}>{formatMetricValue(String(label), value)}</span>
              </div>
            </div>
          ))}
        </div>
      </div>
      <div className={styles.summaryLabel}>TOP PASSES ({String(topPassCount).padStart(2, '0')})</div>
      {topPassCount === 0 ? (
        <div className={styles.tabMessage}>No top passes available.</div>
      ) : (
        <div className={styles.passGroups}>
          {topPassGroups.map(group => (
            <div key={group.key} className={styles.passGroup}>
              <button
                type="button"
                className={styles.passGroupHeader}
                onClick={() => setCollapsedGroups(prev => ({ ...prev, [group.key]: !prev[group.key] }))}
                aria-expanded={!collapsedGroups[group.key]}
              >
                <span className={`${styles.passGroupChevron} ${collapsedGroups[group.key] ? styles.passGroupChevronCollapsed : ''}`} />
                <span>{group.label}</span>
              </button>
              {!collapsedGroups[group.key] && (
                <div className={styles.passList}>
                  {group.items.map((item, index) => {
                    const aos = pick(item, ['aosUtc', 'startTime', 'start', 'timestamp'], null);
                    const durationSec = pick(item, ['durationSec', 'duration', 'durationSeconds'], 0);
                    const startEpoch = toEpoch(aos);
                    const endEpoch = startEpoch != null ? startEpoch + Number(durationSec) : null;
                    const isOngoing = currentEpoch != null && startEpoch != null && endEpoch != null &&
                      currentEpoch >= startEpoch && currentEpoch <= endEpoch;

                    return (
                      <div
                        key={item.id ?? item.passId ?? `${group.key}-${index}`}
                        className={`${styles.passRow} ${isOngoing ? styles.passRowOngoing : ''}`}
                      >
                        <span className={styles.passScore}>
                          {pick(item, ['maxElevationDeg', 'maxElevation'], null) == null
                            ? pick(item, ['score', 'rank', 'quality'], index + 1)
                            : formatElevation(pick(item, ['maxElevationDeg', 'maxElevation'], null))}
                        </span>
                        <span className={styles.passInfo}>
                          <strong>{formatPassPairName(item, index, entityNames)}</strong>
                          <small>{formatTime(pick(item, ['aosUtc', 'startTime', 'start', 'timestamp'], null))}</small>
                        </span>
                        <span>{formatDuration(pick(item, ['durationSec', 'duration', 'durationSeconds'], '-'))}</span>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const EventsContent: React.FC<{
  data: any;
  loading: boolean;
  entityNames: EntityNameMaps;
  allFilters: AllFilterType;
  onPageChange: (page: number) => void;
  currentEpoch: number | null;
}> = ({ data, loading, entityNames, allFilters, onPageChange, currentEpoch }) => {
  const [selectedEventKey, setSelectedEventKey] = useState<string | null>(null);
  const nextPageRequestedRef = useRef(false);
  const events = getList(data);
  const initialLoading = loading && events.length === 0;
  const { page, size, totalCount } = getPageMeta(data);
  const totalPages = size > 0 ? Math.max(1, Math.ceil(totalCount / size)) : 1;
  const canNext = page + 1 < totalPages;

  useEffect(() => {
    if (!loading) nextPageRequestedRef.current = false;
  }, [loading, page]);

  const filteredEvents = useMemo(() => {
    return events;
  }, [events]);

  if (initialLoading) return <div className={styles.tabMessage}>Loading events...</div>;

  const handleScroll = (event: React.UIEvent<HTMLDivElement>) => {
    const el = event.currentTarget;
    if (!canNext || loading || nextPageRequestedRef.current) return;
    if (el.scrollTop + el.clientHeight >= el.scrollHeight - 96) {
      nextPageRequestedRef.current = true;
      onPageChange(page + 1);
    }
  };
  return (
    <div className={styles.eventsTab}>
      <div className={styles.tabSectionHeader}>
        <span>EVENTS</span>
        <span>{filteredEvents.length} / {totalCount} PASSES</span>
      </div>
      {filteredEvents.length === 0 ? (
        <div className={styles.tabMessage}>No events available.</div>
      ) : (
        <div className={styles.eventsTable} onScroll={handleScroll}>
          <div className={styles.eventsHead}>
            <span>#</span>
            <span>PAIR</span>
            <span>START TIME</span>
            <span>END TIME</span>
            <span>DURATION</span>
          </div>
          {filteredEvents.map((event, index) => {
            const startEpoch = toEpoch(pick(event, ['aosUtc', 'startTime', 'start'], null));
            const endEpoch = toEpoch(pick(event, ['losUtc', 'endTime', 'end'], null));
            const isOngoing = currentEpoch != null && startEpoch != null && endEpoch != null &&
              currentEpoch >= startEpoch && currentEpoch <= endEpoch;

            return (
              <React.Fragment key={event.eventId ?? event.id ?? event.passId ?? index}>
                <button
                  type="button"
                  className={`${styles.eventsRow} ${selectedEventKey === String(event.eventId ?? event.id ?? event.passId ?? index) ? styles.eventsRowActive : ''} ${isOngoing ? styles.eventsRowOngoing : ''}`}
                  onClick={() => {
                    const key = String(event.eventId ?? event.id ?? event.passId ?? index);
                    setSelectedEventKey(prev => prev === key ? null : key);
                  }}
                >
                  <span>{String(index + 1).padStart(2, '0')}</span>
                  <span>{formatPassPairName(event, index, entityNames)}</span>
                  <span>{formatTime(pick(event, ['aosUtc', 'startTime', 'start'], null))}</span>
                  <span>{formatTime(pick(event, ['losUtc', 'endTime', 'end'], null))}</span>
                  <span>{formatDuration(pick(event, ['durationSec', 'duration', 'durationSeconds'], '-'))}</span>
                </button>
                {selectedEventKey === String(event.eventId ?? event.id ?? event.passId ?? index) && (
                  <EventSamples event={event} currentEpoch={currentEpoch} />
                )}
              </React.Fragment>
            );
          })}
        </div>
      )}
      {totalPages > 1 && (
        <div className={styles.eventsPagination}>
          <span>{loading ? 'Loading more...' : canNext ? 'Scroll for more' : 'All events loaded'} · {Math.min(filteredEvents.length, totalCount)} / {totalCount}</span>
        </div>
      )}
    </div>
  );
};

const EventSamples: React.FC<{ event: any; currentEpoch: number | null }> = ({ event, currentEpoch }) => {
  const samples = getList(event?.aer ?? event?.samples ?? event?.points ?? event?.timeline);

  const closestSampleIndex = useMemo(() => {
    if (currentEpoch == null || samples.length === 0) return -1;
    const startEpoch = toEpoch(pick(event, ['aosUtc', 'startTime', 'start'], null));
    const endEpoch = toEpoch(pick(event, ['losUtc', 'endTime', 'end'], null));
    if (startEpoch == null || endEpoch == null) return -1;
    if (currentEpoch < startEpoch || currentEpoch > endEpoch) return -1;

    let bestIndex = 0;
    let minDiff = Infinity;
    samples.forEach((sample, idx) => {
      const sampleEpoch = toEpoch(pick(sample, ['timeUtc', 'time', 'timestamp'], null));
      if (sampleEpoch != null) {
        const diff = Math.abs(sampleEpoch - currentEpoch);
        if (diff < minDiff) {
          minDiff = diff;
          bestIndex = idx;
        }
      }
    });
    return bestIndex;
  }, [samples, currentEpoch, event]);

  if (samples.length === 0) {
    return <div className={styles.eventSamplesEmpty}>No AER samples available.</div>;
  }

  return (
    <div className={styles.eventSamples}>
      <div>TIME (UTC)</div>
      <div>AZIMUTH</div>
      <div>ELEVATION</div>
      {samples.map((sample, index) => {
        const isOngoing = index === closestSampleIndex;
        const highlightClass = isOngoing ? styles.sampleOngoing : '';
        return (
          <React.Fragment key={sample.id ?? sample.timestamp ?? index}>
            <span className={highlightClass}>{formatTime(pick(sample, ['timeUtc', 'time', 'timestamp'], null))}</span>
            <span className={highlightClass}>{formatDegrees(pick(sample, ['azimuthDeg', 'azimuth', 'az'], null))}</span>
            <span className={highlightClass}>{formatDegrees(pick(sample, ['elevationDeg', 'elevation', 'el'], null))}</span>
          </React.Fragment>
        );
      })}
    </div>
  );
};

const PlotsContent: React.FC<{
  data: any;
  loading: boolean;
  groundStationOptions: PlotGroundStationOption[];
  selectedSatellitesByGS: Map<string, Set<string>>;
  onSatelliteToggle: (gsId: string, satId: string) => void;
  currentEpoch: number | null;
}> = ({
  data,
  loading,
  groundStationOptions,
  selectedSatellitesByGS,
  onSatelliteToggle,
  currentEpoch,
}) => {
    const allPlots = normalizePlotItems(data).filter(hasPlotData);
    const [passFilterByGS, setPassFilterByGS] = useState<Map<string, 'all' | number>>(new Map());

    // Build satellite options per ground station
    const getSatellitesForGS = (gsId: string) => {
      const gsPlotsForSat = allPlots.filter(p => p.groundStationId === gsId);
      const uniqueSats = Array.from(new Set(gsPlotsForSat.map(p => p.satelliteId)));
      return uniqueSats.map(satId => ({
        id: satId,
        name: allPlots.find(p => p.satelliteId === satId)?.satelliteName || satId,
      }));
    };

    // Get pass count for a satellite
    const getPassCountForSat = (gsId: string, satId: string | undefined) => {
      if (!satId) return 0;
      const passes = allPlots.filter(p => p.groundStationId === gsId && p.satelliteId === satId);
      return passes.length;
    };

    if (groundStationOptions.length === 0) {
      return <div className={styles.tabMessage}>No ground stations available to view plots.</div>;
    }

    return (
      <div className={styles.plotsTab}>
        {loading && <div className={styles.tabMessage}>Loading plots...</div>}

        {!loading && groundStationOptions.map(gsOption => {
          const satellites = getSatellitesForGS(gsOption.id);
          const selectedSatSet = selectedSatellitesByGS.get(gsOption.id);
          const selectedSatId = selectedSatSet && selectedSatSet.size > 0
            ? Array.from(selectedSatSet)[0]
            : satellites[0]?.id;
          const allSatPlots = allPlots.filter(p =>
            p.groundStationId === gsOption.id && p.satelliteId === selectedSatId
          );

          const passFilter = passFilterByGS.get(gsOption.id) ?? 'all';
          const gsPlotsToShow = passFilter === 'all'
            ? allSatPlots
            : allSatPlots.slice(passFilter, passFilter + 1);

          const passCount = getPassCountForSat(gsOption.id, selectedSatId);

          return (
            <div key={gsOption.id} className={styles.groundStationGroup}>
              <div className={styles.groundStationHeader}>
                <h3 className={styles.groundStationTitle}>{gsOption.label}</h3>
                <div className={styles.headerControls}>
                  <select
                    className={styles.groundStationSelect}
                    value={selectedSatId || ''}
                    onChange={(e) => onSatelliteToggle(gsOption.id, e.target.value)}
                  >
                    {satellites.map(sat => (
                      <option key={sat.id} value={sat.id}>
                        {sat.name}
                      </option>
                    ))}
                  </select>
                  <select
                    className={styles.passFilterSelect}
                    value={passFilter === 'all' ? 'all' : passFilter}
                    onChange={(e) => setPassFilterByGS(prev => new Map(prev).set(gsOption.id, e.target.value === 'all' ? 'all' : parseInt(e.target.value)))}
                  >
                    <option value="all">ALL PASSES</option>
                    {passCount > 0 && Array.from({ length: passCount }, (_, i) => (
                      <option key={i} value={i}>
                        PASS {i + 1}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              {gsPlotsToShow.length === 0 ? (
                <div className={styles.groundStationEmpty}>No data available for selected satellite</div>
              ) : (
                <div className={styles.groundStationPlots}>
                  <div className={styles.plotCard}>
                    <div className={styles.plotHeader}>
                      <div className={styles.plotTitleBlock}>
                        <span className={styles.plotTitle}>{gsPlotsToShow[0]?.satelliteName || 'Satellite'}</span>
                        <span className={styles.plotSubtitle}>{gsOption.label} - {gsPlotsToShow.length} Pass{gsPlotsToShow.length !== 1 ? 'es' : ''}</span>
                      </div>
                    </div>
                    <div className={styles.plotBody}>
                      {(() => {
                        // Combine all AER points from all passes
                        const allAerPoints: Array<AerPoint & { passIndex?: number }> = [];
                        gsPlotsToShow.forEach((plot, passIdx) => {
                          const aerPoints = getAerPoints(plot);
                          aerPoints.forEach(point => {
                            allAerPoints.push({
                              ...point,
                              passIndex: passIdx,
                            });
                          });
                        });

                        if (allAerPoints.length === 0) {
                          return <text x="110" y="112" textAnchor="middle" className={styles.polarEmptyText}>No AER points</text>;
                        }

                        // Calculate statistics from combined points
                        const maxElevationPoint = allAerPoints.reduce<typeof allAerPoints[0] | null>((maxPoint, point) => (
                          !maxPoint || point.elevationDeg > maxPoint.elevationDeg ? point : maxPoint
                        ), null);
                        const minElevation = allAerPoints.reduce<number | null>((minValue, point) => (
                          minValue == null || point.elevationDeg < minValue ? point.elevationDeg : minValue
                        ), null);
                        const maxElevation = maxElevationPoint?.elevationDeg ?? null;

                        // Create paths for each pass with different styling
                        const pathsByPass = new Map<number, Array<typeof allAerPoints[0]>>();
                        allAerPoints.forEach(point => {
                          const passIdx = point.passIndex ?? 0;
                          if (!pathsByPass.has(passIdx)) {
                            pathsByPass.set(passIdx, []);
                          }
                          pathsByPass.get(passIdx)!.push(point);
                        });

                        return (
                          <>
                            <div className={styles.plotStats}>
                              <span><b>Max</b>{formatPlotDegreeValue(maxElevation)}</span>
                              <span><b>Min</b>{formatPlotDegreeValue(minElevation)}</span>
                            </div>
                            <svg className={styles.polarPlot} viewBox="0 0 220 220" role="img" aria-label="AER polar plot">
                              {[95, 76, 57, 38, 19].map(radius => <circle key={radius} cx="110" cy="110" r={radius} className={styles.polarGridCircle} />)}
                              {[0, 30, 60, 90, 120, 150].map(angle => {
                                const rad = (angle * Math.PI) / 180;
                                return <line key={angle} x1="110" y1="110" x2={110 + Math.cos(rad) * 95} y2={110 - Math.sin(rad) * 95} className={styles.polarGridLine} />;
                              })}
                              {Array.from(pathsByPass.entries()).map(([passIdx, passPoints]) => {
                                const polarPath = getPolarPath(passPoints as any);
                                const colors = ['#72aeff', '#f5aa2e', '#ccf54e', '#a78bfa', '#34d399'];
                                const color = colors[passIdx % colors.length];

                                // Determine if this pass contains the current ongoing time
                                const startEpoch = passPoints.length > 0 ? toEpoch(passPoints[0].timeUtc) : null;
                                const endEpoch = passPoints.length > 0 ? toEpoch(passPoints[passPoints.length - 1].timeUtc) : null;
                                const isPassOngoing = currentEpoch != null && startEpoch != null && endEpoch != null &&
                                  currentEpoch >= startEpoch && currentEpoch <= endEpoch;

                                // Find closest point in this pass if it is ongoing
                                const closestPoint = isPassOngoing ? passPoints.reduce((best, pt) => {
                                  const bestDiff = Math.abs((toEpoch(best.timeUtc) ?? 0) - currentEpoch);
                                  const ptDiff = Math.abs((toEpoch(pt.timeUtc) ?? 0) - currentEpoch);
                                  return ptDiff < bestDiff ? pt : best;
                                }) : null;

                                return (
                                  <g key={`pass-${passIdx}`}>
                                    {polarPath && (
                                      <path
                                        d={polarPath}
                                        className={`${styles.polarTrack} ${isPassOngoing ? styles.polarTrackOngoing : ''}`}
                                        style={{ stroke: color, strokeWidth: isPassOngoing ? 3.5 : undefined }}
                                      />
                                    )}
                                    {passPoints.length > 0 && (
                                      <>
                                        {/* AOS marker (first point) */}
                                        {(() => {
                                          const aosPoint = passPoints[0];
                                          const coord = getPolarPoint(aosPoint);
                                          return (
                                            <g>
                                              <circle
                                                cx={coord.x}
                                                cy={coord.y}
                                                r={5}
                                                className={styles.aosDot}
                                              />
                                              <title>{`AOS - Pass ${passIdx + 1}`}</title>
                                            </g>
                                          );
                                        })()}
                                        {/* LOS marker (last point) */}
                                        {(() => {
                                          const losPoint = passPoints[passPoints.length - 1];
                                          const coord = getPolarPoint(losPoint);
                                          return (
                                            <g>
                                              <circle
                                                cx={coord.x}
                                                cy={coord.y}
                                                r={5}
                                                className={styles.losDot}
                                              />
                                              <title>{`LOS - Pass ${passIdx + 1}`}</title>
                                            </g>
                                          );
                                        })()}
                                      </>
                                    )}
                                    {closestPoint && (() => {
                                      const coord = getPolarPoint(closestPoint);
                                      return (
                                        <g>
                                          <circle
                                            cx={coord.x}
                                            cy={coord.y}
                                            r={7}
                                            className={styles.ongoingPlotDotHalo}
                                            style={{ fill: color }}
                                          />
                                          <circle
                                            cx={coord.x}
                                            cy={coord.y}
                                            r={4}
                                            className={styles.ongoingPlotDot}
                                            style={{ fill: '#ccf54e' }}
                                          />
                                          <title>{`Current Position: Az=${closestPoint.azimuthDeg.toFixed(1)}°, El=${closestPoint.elevationDeg.toFixed(1)}°`}</title>
                                        </g>
                                      );
                                    })()}
                                  </g>
                                );
                              })}
                              {allAerPoints.map((point, pointIndex) => {
                                const coord = getPolarPoint(point);
                                return (
                                  <g key={`${point.azimuthDeg}-${point.elevationDeg}-${point.passIndex}-${pointIndex}`} className={styles.polarMarker}>
                                    <circle cx={coord.x} cy={coord.y} r="3" className={styles.polarPoint} />
                                    <title>Pass {(point.passIndex ?? 0) + 1}{'\n'}{formatPolarPointTooltip(point, pointIndex)}</title>
                                  </g>
                                );
                              })}
                              {maxElevationPoint && (() => {
                                const coord = getPolarPoint(maxElevationPoint);
                                return (
                                  <g key="max-elevation-marker" className={styles.polarMarker}>
                                    <circle cx={coord.x} cy={coord.y} r={5} className={styles.maxDot} />
                                    <title>Max Elevation: {formatPlotDegreeValue(maxElevationPoint.elevationDeg)}</title>
                                  </g>
                                );
                              })()}
                            </svg>
                            <div className={styles.markerLegend}>
                              <div className={styles.markerLegendItem}>
                                <span className={styles.markerDot} style={{ backgroundColor: '#f5aa2e' }} />
                                <span>AOS</span>
                              </div>
                              <div className={styles.markerLegendItem}>
                                <span className={styles.markerDot} style={{ backgroundColor: '#72aeff' }} />
                                <span>LOS</span>
                              </div>
                              <div className={styles.markerLegendItem}>
                                <span className={styles.markerDot} style={{ backgroundColor: '#ccf54e' }} />
                                <span>Max Elevation</span>
                              </div>
                            </div>
                            {gsPlotsToShow.length > 1 && (
                              <div className={styles.plotLegend}>
                                {Array.from(pathsByPass.keys()).map((passIdx) => {
                                  const colors = ['#72aeff', '#f5aa2e', '#ccf54e', '#a78bfa', '#34d399'];
                                  const color = colors[passIdx % colors.length];
                                  return (
                                    <div key={`pass-legend-${passIdx}`} className={styles.legendItem}>
                                      <span className={styles.legendColor} style={{ backgroundColor: color }} />
                                      <span>Pass {passIdx + 1}</span>
                                    </div>
                                  );
                                })}
                              </div>
                            )}
                          </>
                        );
                      })()}
                    </div>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    );
  };

const AccessAnalysisReportsPanel: React.FC = () => {
  const {
    accessAnalysisReports,
    timelineCollapsed,
    timelineHeight,
    satellites,
    geoFeatures,
    groundStations,
    setAccessAnalysisReports,
    setReportHoverHighlights,
    selectedReportId,
    setSelectedReportId,
    selectedReportDetail,
    setSelectedReportDetail,
    summaryData,
    setSummaryData,
    eventsData,
    setEventsData,
    allFilters,
    setAllFilters,
    drawerMode,
    setDrawerMode,
    coverageAnalysisReports,
    setCoverageAnalysisReports,
    selectedCoverageReportId,
    setSelectedCoverageReportId,
    selectedCoverageReportDetail,
    setSelectedCoverageReportDetail,
    upsertCoverageAnalysisReport,
    coverageSectionTab,
    setCoverageSectionTab,
    heatmapMetricMode,
    setHeatmapMetricMode,
    revisitThresholds,
    triggerTimelineReset,
  } = useMDCockpit();
  const cesium = useCesium();
  const currentTimeStr = useCesiumClock(cesium.viewerRef);

  const [userData, setUserData] = useState<any>(null);

  useEffect(() => {
    if (sessionStorage.getItem("userDetails")) {
      const user = JSON.parse(sessionStorage.getItem("userDetails") || "");
      setUserData(user);
    }
  }, []);

  const isMMAnalysisEnabled = useMemo(
    () => userData?.["feature-allowed"]?.["mmAIAnalysis"] === "Yes",
    [userData]
  );


  const currentEpoch = useMemo(() => {
    if (!currentTimeStr) return null;
    const isoStr = currentTimeStr.replace(' ', 'T') + 'Z';
    const timeMs = Date.parse(isoStr);
    return Number.isNaN(timeMs) ? null : Math.floor(timeMs / 1000);
  }, [currentTimeStr]);

  const { mission_id, scenario_id } = useParams<{ mission_id: string; scenario_id: string }>();
  const [open, setOpen] = useState(false);
  const [deletingReportId, setDeletingReportId] = useState<string | null>(null);
  const [pendingDeleteReport, setPendingDeleteReport] = useState<{ id: string; name: string; type: 'access' | 'coverage' } | null>(null);
  const [loadingReportId, setLoadingReportId] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<ReportTab>('Summary');
  const [eventsPage, setEventsPage] = useState(0);
  const [plotsData, setPlotsData] = useState<any>(null);
  const [selectedSatellitesByGS, setSelectedSatellitesByGS] = useState<Map<string, Set<string>>>(new Map());
  const [tabLoading, setTabLoading] = useState<ReportTab | null>(null);
  const [aiExpanded, setAiExpanded] = useState(false);
  const [aiInputFocused, setAiInputFocused] = useState(false);
  const [aiPrompt, setAiPrompt] = useState('');
  const [aiMessages, setAiMessages] = useState<AIChatMessage[]>([]);
  const [aiLoading, setAiLoading] = useState(false);
  const [aiDetached, setAiDetached] = useState(false);
  const [editingAIMessageIndex, setEditingAIMessageIndex] = useState<number | null>(null);
  const [editingAIMessageText, setEditingAIMessageText] = useState('');
  const [aiSplashVisible, setAiSplashVisible] = useState(false);
  const [aiPlaceholderIndex, setAiPlaceholderIndex] = useState(0);
  const [aiPlaceholderText, setAiPlaceholderText] = useState('');
  const [aiLoadingMessageIndex, setAiLoadingMessageIndex] = useState(0);
  const [panelWidth, setPanelWidth] = useState<number | null>(null);
  const [resizing, setResizing] = useState(false);

  // Coverage Analysis states
  const [reportsTab, setReportsTab] = useState<'access' | 'coverage'>('access');
  const [selectedAoiId, setSelectedAoiId] = useState<string | null>(null);
  const [coveragePage, setCoveragePage] = useState(0);
  const [selectedSatFilter, setSelectedSatFilter] = useState('');
  const [selectedSatDropdownValue, setSelectedSatDropdownValue] = useState('All');

  const isTargetTrack = useMemo(() => {
    if (!selectedAoiId) return false;
    const feature = geoFeatures.find(f => f.id === selectedAoiId);
    return feature?.type === 'Target Track';
  }, [selectedAoiId, geoFeatures]);

  useEffect(() => {
    setSelectedSatFilter('');
    setSelectedSatDropdownValue('All');
    setCoveragePage(0);

    if (selectedCoverageReportDetail) {
      const aois = selectedCoverageReportDetail.raw?.requestInput?.aoiobjects ?? [];
      if (aois.length > 0) {
        setSelectedAoiId(aois[0].aoiId);
      } else {
        setSelectedAoiId(null);
      }
    } else {
      setSelectedAoiId(null);
    }
  }, [selectedCoverageReportId, selectedCoverageReportDetail]);
  const [simpleCoverageData, setSimpleCoverageData] = useState<any>(null);
  const [gapSummaryData, setGapSummaryData] = useState<any>(null);
  const [revisitSummaryData, setRevisitSummaryData] = useState<any>(null);
  const [coverageVisualizationPasses, setCoverageVisualizationPasses] = useState<any>(null);
  const [gapVisualizationSpots, setGapVisualizationSpots] = useState<any>(null);
  const [gapTableMode, setGapTableMode] = useState<'windows' | 'spots'>('windows');
  const [coverageLoading, setCoverageLoading] = useState(false);
  const [coverageError, setCoverageError] = useState<string | null>(null);
  const [renamingCoverageReportId, setRenamingCoverageReportId] = useState<string | null>(null);
  const [renamingName, setRenamingName] = useState('');

  useEffect(() => {
    if (isTargetTrack && coverageSectionTab === 'simple') {
      setCoverageSectionTab('gap');
    }
  }, [isTargetTrack, coverageSectionTab, setCoverageSectionTab]);

  const lastFetchedCoverageKeyRef = useRef<string>('');

  useEffect(() => {
    // Clear all tab cache when report, AOI, page, or filters change
    setSimpleCoverageData(null);
    setCoverageVisualizationPasses(null);
    setGapSummaryData(null);
    setGapVisualizationSpots(null);
    setRevisitSummaryData(null);
    setCoverageError(null);
  }, [selectedCoverageReportId, selectedAoiId, selectedSatFilter, coveragePage]);

  useEffect(() => {
    // Clear error when switching sub-tabs (Simple Coverage, Gap, Revisits)
    setCoverageError(null);
  }, [coverageSectionTab]);

  const triggerTimelineResetRef = useRef(triggerTimelineReset);
  useEffect(() => {
    triggerTimelineResetRef.current = triggerTimelineReset;
  });

  useEffect(() => {
    triggerTimelineResetRef.current();
  }, [activeTab, coverageSectionTab, reportsTab, selectedReportId, selectedCoverageReportId]);

  const panelRef = useRef<HTMLDivElement>(null);
  const lastFetchedFiltersRef = useRef<string>('[]');

  useEffect(() => {
    if (!selectedReportDetail) return;

    const timer = setTimeout(() => {
      if (!panelRef.current) return;

      const disabledElements: { el: Element; originalClasses: string[] }[] = [];
      const classesToDisable = [
        'filter__container',
        'advance__filter__modal__container',
        'filter__icon__container',
        'applied__filters__container'
      ];

      classesToDisable.forEach(className => {
        const elements = document.querySelectorAll(`.${className}`);
        elements.forEach(el => {
          if (!panelRef.current?.contains(el)) {
            const originalClasses = Array.from(el.classList);
            disabledElements.push({ el, originalClasses });
            el.classList.remove(className);
            el.classList.add(`${className}_temp_disabled`);
          }
        });
      });

      (window as any).__accessAnalysisReportsFilterCleanup = () => {
        disabledElements.forEach(({ el, originalClasses }) => {
          classesToDisable.forEach(className => {
            if (originalClasses.includes(className)) {
              el.classList.add(className);
              el.classList.remove(`${className}_temp_disabled`);
            }
          });
        });
      };
    }, 100);

    return () => {
      clearTimeout(timer);
      if (typeof (window as any).__accessAnalysisReportsFilterCleanup === 'function') {
        (window as any).__accessAnalysisReportsFilterCleanup();
        delete (window as any).__accessAnalysisReportsFilterCleanup;
      }
    };
  }, [selectedReportDetail]);

  const hasProgress = accessAnalysisReports.some(report => !isComplete(report) && !isFailed(report));
  const resolvedPanelWidth = panelWidth ?? ((selectedReportDetail || selectedCoverageReportDetail) ? 580 : 336);
  const timelineOffset = timelineCollapsed ? 82 : timelineHeight + 52;
  const detailInputCards = buildInputCards(
    getReportInput(selectedReportDetail),
    geoFeatures as GeoFeature[],
    satellites as Array<Satellite | SatGroup>,
  );
  const entityNames = buildEntityNameMaps(
    satellites as Array<Satellite | SatGroup>,
    groundStations as GroundStation[],
    geoFeatures as GeoFeature[],
  );
  const plotGroundStationOptions = buildPlotGroundStationOptions(selectedReportDetail, entityNames);
  const activeReport = selectedReportDetail || selectedCoverageReportDetail;
  const usedEntities = useMemo(() => getReportUsedEntities(activeReport, satellites as Array<Satellite | SatGroup>), [activeReport, satellites]);

  const availableSatellites = useMemo(() => {
    if (!selectedReportDetail) return [];
    const input = getReportInput(selectedReportDetail);
    const sourceEntries = getSourceEntries(input);
    const activeSatIds = new Set<string>();

    sourceEntries.forEach((source) => {
      const item = findPlotSourceItem(satellites as Array<Satellite | SatGroup>, source.id, source.type);
      if (item && (item as SatGroup).type === 'group') {
        (item as SatGroup).children.forEach((child) => activeSatIds.add(child.id));
      } else if (item) {
        activeSatIds.add(item.id);
      } else {
        activeSatIds.add(source.id);
      }
    });

    const list: { id: string; name: string }[] = [];
    (satellites as Array<Satellite | SatGroup>).forEach((item) => {
      if ((item as SatGroup).type === 'group') {
        (item as SatGroup).children.forEach((child) => {
          if (activeSatIds.has(child.id)) {
            list.push({ id: child.id, name: child.name });
          }
        });
      } else {
        const sat = item as Satellite;
        if (activeSatIds.has(sat.id)) {
          list.push({ id: sat.id, name: sat.name });
        }
      }
    });

    activeSatIds.forEach(id => {
      if (!list.some(item => item.id === id)) {
        const name = entityNames.satellites.get(id) ?? id;
        list.push({ id, name });
      }
    });

    const seen = new Set<string>();
    return list.filter((item) => {
      if (seen.has(item.id)) return false;
      seen.add(item.id);
      return true;
    });
  }, [selectedReportDetail, satellites, entityNames]);

  const availableGroundStations = useMemo(() => {
    if (!selectedReportDetail) return [];
    const input = getReportInput(selectedReportDetail);
    const gsIds = new Set(getIdList(input?.groundstationIds, input?.groundStationIds, input?.ground_station_ids));

    const list = (groundStations as GroundStation[])
      .filter(gs => gsIds.has(gs.id))
      .map(gs => ({ id: gs.id, name: gs.name }));

    gsIds.forEach(id => {
      if (!list.some(item => item.id === id)) {
        const name = entityNames.groundStations.get(id) ?? id;
        list.push({ id, name });
      }
    });

    return list;
  }, [selectedReportDetail, groundStations, entityNames]);

  const availableTargetGeofeatures = useMemo(() => {
    if (!selectedReportDetail) return [];
    const input = getReportInput(selectedReportDetail);
    const aoiIds = new Set(getIdList(input?.aoiIds, input?.aoi_ids, input?.geoFeatureIds, input?.geo_feature_ids));

    const list = (geoFeatures as GeoFeature[])
      .filter(feat => aoiIds.has(feat.id) && (feat.kind === 'point' || feat.kind === 'circle'))
      .map(feat => ({ id: feat.id, name: feat.name }));

    aoiIds.forEach(id => {
      const feat = geoFeatures.find(f => f.id === id);
      const kind = feat?.kind;
      if (feat && kind && kind !== 'polygon') {
        if (!list.some(item => item.id === id)) {
          list.push({ id, name: feat.name });
        }
      } else if (!feat) {
        if (!list.some(item => item.id === id)) {
          const name = entityNames.geoFeatures.get(id) ?? id;
          list.push({ id, name });
        }
      }
    });

    return list;
  }, [selectedReportDetail, geoFeatures, entityNames]);

  const availableAreaGeofeatures = useMemo(() => {
    if (!selectedReportDetail) return [];
    const input = getReportInput(selectedReportDetail);
    const aoiIds = new Set(getIdList(input?.aoiIds, input?.aoi_ids, input?.geoFeatureIds, input?.geo_feature_ids));

    const list = (geoFeatures as GeoFeature[])
      .filter(feat => aoiIds.has(feat.id) && feat.kind === 'polygon')
      .map(feat => ({ id: feat.id, name: feat.name }));

    aoiIds.forEach(id => {
      const feat = geoFeatures.find(f => f.id === id);
      const kind = feat?.kind;
      if (feat && kind === 'polygon') {
        if (!list.some(item => item.id === id)) {
          list.push({ id, name: feat.name });
        }
      }
    });

    return list;
  }, [selectedReportDetail, geoFeatures, entityNames]);

  const filterList = useMemo<FilterListType>(() => {
    const operatorConfig = {
      placeholder: 'Operator',
      options: [
        { label: '= Equals', value: '=' },
        { label: '≠ not Equals', value: '≠' },
      ],
    };

    return {
      placeholder: 'Property',
      options: [
        {
          label: 'Satellite',
          value: 'Satellite',
          operator: operatorConfig,
          properties: {
            placeholder: 'Value',
            enabledSearch: true,
            multiselection: true,
            options: availableSatellites.map(sat => ({ label: sat.name, value: sat.id })),
          },
        },
        {
          label: 'Ground Station',
          value: 'Ground Station',
          operator: operatorConfig,
          properties: {
            placeholder: 'Value',
            enabledSearch: true,
            multiselection: true,
            options: availableGroundStations.map(gs => ({ label: gs.name, value: gs.id })),
          },
        },
        {
          label: 'Geofence',
          value: 'Geofence',
          operator: operatorConfig,
          properties: {
            placeholder: 'Value',
            enabledSearch: true,
            multiselection: true,
            options: availableAreaGeofeatures.map(tgt => ({ label: tgt.name, value: tgt.id })),
          },
        },
        {
          label: 'Target Track',
          value: 'Target Track',
          operator: operatorConfig,
          properties: {
            placeholder: 'Value',
            enabledSearch: true,
            multiselection: true,
            options: availableTargetGeofeatures.map(tgt => ({ label: tgt.name, value: tgt.id })),
          },
        },
        {
          label: 'Object Pair',
          value: 'Object Pair',
          operator: {
            placeholder: 'Operator',
            options: [
              { label: '= Equals', value: '=' }
            ],
          },
          properties: {
            placeholder: 'Value',
            enabledSearch: true,
            multiselection: false,
            options: [
              { label: 'Sat → Spot target', value: 'PAIR_SAT_TO_TARGET' },
              { label: 'Sat → Geofence', value: 'PAIR_SAT_TO_GEOFENCE' },
              { label: 'Sat → Geofeature', value: 'PAIR_SAT_TO_GEOFEATURE' },
              { label: 'Sat → Ground station', value: 'PAIR_SAT_TO_GS' },
              { label: 'Sat → Sat (ISL)', value: 'PAIR_SAT_TO_SAT' },
            ],
          },
        },
        {
          label: 'Geofeature',
          value: 'Geofeature',
          operator: operatorConfig,
          properties: {
            placeholder: 'Value',
            enabledSearch: true,
            multiselection: true,
            options: [...availableTargetGeofeatures, ...availableAreaGeofeatures].map(tgt => ({ label: tgt.name, value: tgt.id })),
          },
        },
      ],
    };
  }, [availableSatellites, availableTargetGeofeatures, availableAreaGeofeatures, availableGroundStations]);

  const apiFilters = useMemo(() => {
    const advanceFilters = allFilters?.advanceFilters || [];
    return advanceFilters.map(filter => {
      const apiProperty = {
        'Object Pair': 'OBJECT_PAIR',
        'Satellite': 'SATELLITE',
        'Ground Station': 'GROUND_STATION',
        'Geofence': 'GEOFENCE',
        'Target Track': 'TARGET',
        'Geofeature': 'GEOFEATURE',
      }[filter.properties] || filter.properties;

      const apiOperator = (filter.operator.includes('≠') || filter.operator.toLowerCase().includes('not'))
        ? 'NOT_EQUALS'
        : 'EQUALS';

      const filterValues = Array.isArray(filter.value) ? filter.value : [filter.value];

      let apiValues: string[] = [];
      if (apiProperty === 'OBJECT_PAIR') {
        apiValues = filterValues.map(v => {
          const map: Record<string, string> = {
            'Sat → Spot target': 'PAIR_SAT_TO_TARGET',
            'Sat → Geofence': 'PAIR_SAT_TO_GEOFENCE',
            'Sat → Geofeature': 'PAIR_SAT_TO_GEOFEATURE',
            'Sat → Ground station': 'PAIR_SAT_TO_GS',
            'Sat → Sat (ISL)': 'PAIR_SAT_TO_SAT',
            'PAIR_SAT_TO_TARGET': 'PAIR_SAT_TO_TARGET',
            'PAIR_SAT_TO_GEOFENCE': 'PAIR_SAT_TO_GEOFENCE',
            'PAIR_SAT_TO_GEOFEATURE': 'PAIR_SAT_TO_GEOFEATURE',
            'PAIR_SAT_TO_GS': 'PAIR_SAT_TO_GS',
            'PAIR_SAT_TO_SAT': 'PAIR_SAT_TO_SAT',
          };
          return map[v] || v;
        });
      } else if (apiProperty === 'SATELLITE') {
        apiValues = filterValues.map(v => {
          const sat = availableSatellites.find(item => item.name === v);
          return sat ? sat.id : v;
        });
      } else if (apiProperty === 'GROUND_STATION') {
        apiValues = filterValues.map(v => {
          const gs = availableGroundStations.find(item => item.name === v);
          return gs ? gs.id : v;
        });
      } else if (apiProperty === 'TARGET' || apiProperty === 'GEOFENCE' || apiProperty === 'GEOFEATURE') {
        apiValues = filterValues.map(v => {
          const combined = [...availableTargetGeofeatures, ...availableAreaGeofeatures];
          const feat = combined.find(item => item.name === v);
          return feat ? feat.id : v;
        });
      } else {
        apiValues = filterValues;
      }

      return {
        property: apiProperty,
        operator: apiOperator,
        values: apiValues,
      };
    });
  }, [allFilters?.advanceFilters, availableSatellites, availableTargetGeofeatures, availableAreaGeofeatures, availableGroundStations]);

  const apiFiltersStr = JSON.stringify(apiFilters);

  useEffect(() => {
    setSummaryData(null);
    setEventsData(null);
    setEventsPage(0);
  }, [apiFiltersStr]);

  useEffect(() => {
    setAllFilters({ advanceFilters: [] });
  }, [selectedReportId]);

  // Auto-select first satellite per ground station when report loads
  useEffect(() => {
    if (!plotsData || plotGroundStationOptions.length === 0) return;

    setSelectedSatellitesByGS(prev => {
      // Only initialize if empty (don't override user selections)
      if (prev.size > 0) return prev;

      const allPlots = normalizePlotItems(plotsData).filter(hasPlotData);
      const newSelectedSats = new Map<string, Set<string>>();

      plotGroundStationOptions.forEach(gsOption => {
        const gsPlotsForSat = allPlots.filter(p => p.groundStationId === gsOption.id);
        const uniqueSats = Array.from(new Set(gsPlotsForSat.map(p => p.satelliteId)));
        if (uniqueSats.length > 0) {
          // Auto-select first satellite
          newSelectedSats.set(gsOption.id, new Set([uniqueSats[0]]));
        }
      });

      return newSelectedSats;
    });
  }, [plotsData, plotGroundStationOptions]);

  const handleSatelliteToggle = (gsId: string, satId: string) => {
    setSelectedSatellitesByGS(prev => {
      const newMap = new Map(prev);
      // For single-select dropdown, replace the selection entirely
      newMap.set(gsId, new Set([satId]));
      return newMap;
    });
  };
  const setInputCardHover = (card: InputSummaryCard | null) => {
    setReportHoverHighlights(card && card.count > 0 ? card.highlights : null);
  };

  const refreshReports = async () => {
    if (!mission_id || !scenario_id) return;
    const res = await MissionService.getAccessAnalysisReports(mission_id, scenario_id);
    const data = res?.data?.data ?? res?.data;
    setAccessAnalysisReports(mapAccessAnalysisReportsResponse(data));
  };

  useEffect(() => {
    const prompt = `${AI_PLACEHOLDER_PROMPTS[aiPlaceholderIndex % AI_PLACEHOLDER_PROMPTS.length]}......`;
    let charIndex = 0;
    let nextPromptTimer: number | undefined;
    setAiPlaceholderText('');

    const typeTimer = window.setInterval(() => {
      charIndex += 1;
      setAiPlaceholderText(prompt.slice(0, charIndex));
      if (charIndex >= prompt.length) {
        window.clearInterval(typeTimer);
        nextPromptTimer = window.setTimeout(() => {
          setAiPlaceholderIndex(index => (index + 1) % AI_PLACEHOLDER_PROMPTS.length);
        }, 2000);
      }
    }, 34);

    return () => {
      window.clearInterval(typeTimer);
      if (nextPromptTimer) window.clearTimeout(nextPromptTimer);
    };
  }, [aiPlaceholderIndex]);

  useEffect(() => {
    if (!aiExpanded) {
      setAiSplashVisible(false);
      return undefined;
    }
    setAiSplashVisible(true);
    const timer = window.setTimeout(() => setAiSplashVisible(false), 780);
    return () => window.clearTimeout(timer);
  }, [aiExpanded, selectedReportDetail?.reportId]);

  useEffect(() => {
    if (!aiLoading) {
      setAiLoadingMessageIndex(0);
      return undefined;
    }
    const timer = window.setInterval(() => {
      setAiLoadingMessageIndex(index => (index + 1) % AI_LOADING_MESSAGES.length);
    }, 1350);
    return () => window.clearInterval(timer);
  }, [aiLoading]);

  useEffect(() => {
    document.documentElement.style.setProperty('--mdai-report-panel-offset', open ? `${resolvedPanelWidth}px` : '0px');
    document.documentElement.style.setProperty('--mdai-report-panel-transition', resizing ? 'none' : 'right 0.3s ease-in-out');
    return () => {
      document.documentElement.style.setProperty('--mdai-report-panel-offset', '0px');
      document.documentElement.style.setProperty('--mdai-report-panel-transition', 'right 0.3s ease-in-out');
    };
  }, [open, resolvedPanelWidth, resizing]);

  const handleResizeStart = (event: React.MouseEvent<HTMLDivElement>) => {
    event.preventDefault();
    const startX = event.clientX;
    const startWidth = resolvedPanelWidth;
    setResizing(true);

    const handleResizeMove = (moveEvent: MouseEvent) => {
      const nextWidth = Math.max(
        REPORT_PANEL_MIN_WIDTH,
        Math.min(REPORT_PANEL_MAX_WIDTH, startWidth + startX - moveEvent.clientX),
      );
      setPanelWidth(nextWidth);
    };

    const handleResizeEnd = () => {
      setResizing(false);
      document.removeEventListener('mousemove', handleResizeMove);
      document.removeEventListener('mouseup', handleResizeEnd);
    };

    document.addEventListener('mousemove', handleResizeMove);
    document.addEventListener('mouseup', handleResizeEnd);
  };

  const handleDeleteReport = async (reportId: string, reportName?: string) => {
    if (!mission_id || !scenario_id || deletingReportId) return;

    setDeletingReportId(reportId);
    try {
      await MissionService.deleteAccessAnalysisReport(mission_id, scenario_id, reportId);
      if (selectedReportId === reportId) {
        setSelectedReportId(null);
        setSelectedReportDetail(null);
      }
      setPendingDeleteReport(null);
      await refreshReports();
      const displayName = reportName ? ` ${reportName}` : '';
      showToastMessage('success', `Access Report${displayName} deleted`);
    } catch (error) {
      const message = (error as any)?.response?.data?.message ?? (error as Error)?.message ?? 'Failed to delete report';
      showToastMessage('error', message);
    } finally {
      setDeletingReportId(null);
    }
  };

  const resetReportView = () => {
    setSelectedReportId(null);
    setSelectedReportDetail(null);
    setSelectedCoverageReportId(null);
    setSelectedCoverageReportDetail(null);
    setPendingDeleteReport(null);
    setSummaryData(null);
    setEventsData(null);
    setEventsPage(0);
    setPlotsData(null);
    setSelectedSatellitesByGS(new Map());
    setAiExpanded(false);
    setAiInputFocused(false);
    setAiDetached(false);
    setAiPrompt('');
    setAiMessages([]);
    setEditingAIMessageIndex(null);
    setEditingAIMessageText('');
    setAiSplashVisible(false);
    setActiveTab('Summary');
  };

  const handleToggleOpen = () => {
    resetReportView();
    setOpen(value => !value);
  };

  const handleSelectReport = async (report: AccessAnalysisReport) => {
    if (!mission_id || !scenario_id || loadingReportId === report.reportId) return;

    setSelectedReportId(report.reportId);
    setActiveTab('Summary');
    setSummaryData(null);
    setEventsData(null);
    setEventsPage(0);
    setPlotsData(null);
    setSelectedSatellitesByGS(new Map());
    setAiExpanded(false);
    setAiInputFocused(false);
    setAiDetached(false);
    setAiPrompt('');
    setAiMessages([]);
    setEditingAIMessageIndex(null);
    setEditingAIMessageText('');
    setAiSplashVisible(false);
    setLoadingReportId(report.reportId);
    try {
      const res = await MissionService.getAccessAnalysisReportById(mission_id, scenario_id, report.reportId);
      const data = res?.data?.data ?? res?.data;
      setSelectedReportDetail({
        ...report,
        name: data?.name ?? data?.reportName ?? data?.report_name ?? report.name,
        status: data?.status ?? data?.reportStatus ?? data?.report_status ?? report.status,
        createdAt: data?.createdAt ?? data?.created_at ?? data?.generatedAt ?? data?.generated_at ?? report.createdAt,
        updatedAt: data?.updatedAt ?? data?.updated_at ?? report.updatedAt,
        raw: data,
      });
    } catch (error) {
      const message = (error as any)?.response?.data?.message ?? (error as Error)?.message ?? 'Failed to fetch report details';
      showToastMessage('error', message);
    } finally {
      setLoadingReportId(null);
    }
  };

  const handleBackToList = () => {
    resetReportView();
  };

  const submitAIQuestion = async (
    question: string,
    options: { appendUserMessage?: boolean; history?: AIChatMessage[]; responseIndex?: number } = {},
  ) => {
    if (!question || !mission_id || !scenario_id || !selectedReportDetail || aiLoading) return;

    setAiExpanded(true);
    setAiLoading(true);
    const history = options.history ?? aiMessages;
    const userMessage: AIChatMessage = { role: 'user', content: question };
    if (options.appendUserMessage !== false) {
      setAiMessages(prev => [...prev, userMessage]);
    }
    setAiPrompt('');

    try {
      const res = await MissionService.chatAccessAnalysisReport(mission_id, scenario_id, {
        question,
        conversation_history: history,
        report_id: selectedReportDetail.reportId,
      });
      const data = res?.data?.data ?? res?.data;
      const assistantMessage: AIChatMessage = { role: 'assistant', content: getChatAnswer(data) };
      setAiMessages((prev) => {
        if (options.responseIndex == null) return [...prev, assistantMessage];
        const next = [...prev];
        const deleteCount = next[options.responseIndex]?.role === 'assistant' ? 1 : 0;
        next.splice(options.responseIndex, deleteCount, assistantMessage);
        return next;
      });
    } catch (error) {
      const message = (error as any)?.response?.data?.message ?? (error as Error)?.message ?? 'Failed to ask Antaris INTELLIGENCE';
      showToastMessage('error', message);
      setAiMessages((prev) => {
        const assistantMessage: AIChatMessage = { role: 'assistant', content: message };
        if (options.responseIndex == null) return [...prev, assistantMessage];
        const next = [...prev];
        const deleteCount = next[options.responseIndex]?.role === 'assistant' ? 1 : 0;
        next.splice(options.responseIndex, deleteCount, assistantMessage);
        return next;
      });
    } finally {
      setAiLoading(false);
    }
  };

  const handleAISubmit = async (event?: React.FormEvent<HTMLFormElement>) => {
    event?.preventDefault();
    const question = aiPrompt.trim();
    if (!question) return;
    setAiPrompt('');
    await submitAIQuestion(question);
  };

  const handleAIComposerKeyDown = (event: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (event.key !== 'Enter' || event.shiftKey) return;
    event.preventDefault();
    void handleAISubmit();
  };

  const copyAIMessage = async (content: string) => {
    try {
      await navigator.clipboard?.writeText(content);
      showToastMessage('success', 'Message copied');
    } catch {
      showToastMessage('error', 'Unable to copy message');
    }
  };

  const handleAIMessageAction = (action: AIMessageAction, message: AIChatMessage, index: number) => {
    if (action === 'copy') {
      void copyAIMessage(message.content);
      return;
    }
    if (action === 'delete') {
      setAiMessages(prev => prev.filter((_, idx) => idx !== index));
      if (editingAIMessageIndex === index) {
        setEditingAIMessageIndex(null);
        setEditingAIMessageText('');
      }
      return;
    }
    if (action === 'edit') {
      setEditingAIMessageIndex(index);
      setEditingAIMessageText(message.content);
      return;
    }
    if (action === 'resend') {
      void submitAIQuestion(message.content, { appendUserMessage: false });
    }
  };

  const handleAIEditCancel = () => {
    setEditingAIMessageIndex(null);
    setEditingAIMessageText('');
  };

  const handleAIEditResend = (index: number) => {
    const nextText = editingAIMessageText.trim();
    if (!nextText || aiLoading) return;
    const nextMessages = aiMessages.map((item, idx) => idx === index ? { ...item, content: nextText } : item);
    const responseIndex = index + 1;
    if (nextMessages[responseIndex]?.role === 'assistant') {
      nextMessages.splice(responseIndex, 1);
    }
    setAiMessages(nextMessages);
    setEditingAIMessageIndex(null);
    setEditingAIMessageText('');
    void submitAIQuestion(nextText, { appendUserMessage: false, history: nextMessages, responseIndex });
  };

  const handleAIEditKeyDown = (event: React.KeyboardEvent<HTMLTextAreaElement>, index: number) => {
    if (event.key !== 'Enter' || event.shiftKey) return;
    event.preventDefault();
    handleAIEditResend(index);
  };

  useEffect(() => {
    if (!selectedReportDetail || !mission_id || !scenario_id) return;
    let cancelled = false;

    const fetchTab = async () => {
      const filtersChanged = lastFetchedFiltersRef.current !== apiFiltersStr;

      if (activeTab === 'Summary' && summaryData && !filtersChanged) return;
      if (activeTab === 'Events' && eventsData && getPageMeta(eventsData).page === eventsPage && !filtersChanged) return;
      if (activeTab === 'Plots' && plotsData) return;

      lastFetchedFiltersRef.current = apiFiltersStr;
      setTabLoading(activeTab);
      try {
        let res: any;
        let data: any;
        if (activeTab === 'Summary') {
          res = await MissionService.getAccessAnalysisReportSummary(mission_id, scenario_id, selectedReportDetail.reportId, apiFilters as any);
        } else if (activeTab === 'Events') {
          res = await MissionService.getAccessAnalysisReportEvents(mission_id, scenario_id, selectedReportDetail.reportId, {
            page: eventsPage,
            size: EVENTS_PAGE_SIZE,
            filters: apiFilters as any,
          });
        } else {
          // Fetch plots for all ground stations and all available satellites
          const reportInput = getReportInput(selectedReportDetail);
          const sourcesData = getSourceEntries(reportInput);
          const allSatelliteIds = Array.from(new Set(
            sourcesData.flatMap((source) => {
              const item = findPlotSourceItem(satellites as Array<Satellite | SatGroup>, source.id, source.type);
              if (item && (item as SatGroup).type === 'group') {
                return (item as SatGroup).children.map(sat => sat.id);
              }
              const sat = item as Satellite | null;
              return [sat?.id || source.id].filter(Boolean);
            })
          ));

          data = await Promise.all(
            plotGroundStationOptions.flatMap(gsOption =>
              allSatelliteIds.map(async (satelliteId) => {
                const plotRes = await MissionService.getAccessAnalysisReportPlots(mission_id, scenario_id, selectedReportDetail.reportId, {
                  satelliteId,
                  groundStationId: gsOption.id,
                  plotType: 'aer',
                });
                const plotData = plotRes?.data?.data ?? plotRes?.data;
                return {
                  satelliteId,
                  satelliteName: entityNames.satellites.get(satelliteId) ?? satelliteId,
                  groundStationId: gsOption.id,
                  groundStationName: gsOption.label,
                  data: plotData,
                };
              })
            )
          );
          data = data.filter((record: any) => normalizePlotItems(record).some(hasPlotData));
        }
        if (cancelled) return;
        if (activeTab !== 'Plots') data = res?.data?.data ?? res?.data;
        if (activeTab === 'Summary') setSummaryData(data);
        if (activeTab === 'Events') {
          setEventsData((prev: any) => eventsPage === 0 ? data : mergeEventPages(prev, data));
        }
        if (activeTab === 'Plots') setPlotsData(data);
      } catch (error) {
        if (!cancelled) {
          const message = (error as any)?.response?.data?.message ?? (error as Error)?.message ?? `Failed to fetch ${activeTab.toLowerCase()} data`;
          showToastMessage('error', message);
        }
      } finally {
        if (!cancelled) setTabLoading(null);
      }
    };

    void fetchTab();
    return () => { cancelled = true; };
  }, [activeTab, selectedReportDetail?.reportId, mission_id, scenario_id, eventsPage, apiFiltersStr]); // eslint-disable-line react-hooks/exhaustive-deps

  const outputInAIMode = aiExpanded && !aiDetached;

  const renderAIWorkspace = (detached = false) => (
    <section className={`${styles.aiAssistant} ${aiExpanded ? styles.aiAssistantExpanded : ''} ${aiInputFocused ? styles.aiAssistantFocused : ''} ${detached ? styles.aiAssistantDetached : ''}`}>
      <form
        className={styles.aiComposer}
        onSubmit={handleAISubmit}
      >
        <textarea
          value={aiPrompt}
          onChange={event => setAiPrompt(event.target.value)}
          onKeyDown={handleAIComposerKeyDown}
          onFocus={() => {
            setAiInputFocused(true);
            setAiExpanded(true);
          }}
          onBlur={() => setAiInputFocused(false)}
          placeholder={aiPlaceholderText || 'Ask Antaris INTELLIGENCE......'}
          rows={1}
        />
        <button
          type="submit"
          aria-label="Ask Antaris INTELLIGENCE"
          title="Ask Antaris INTELLIGENCE"
          disabled={aiLoading || !aiPrompt.trim()}
        >
          <AISparkleIcon />
        </button>
      </form>
      {aiExpanded && (
        <div className={styles.aiOutputPanel}>
          <div className={styles.aiOutputHeader}>
            <span className={styles.aiOutputTitle}>ANTARIS INTELLIGENCE</span>
            <div className={styles.aiOutputActions}>
              <button
                type="button"
                title={detached ? 'Attach AI panel' : 'Detach AI panel'}
                aria-label={detached ? 'Attach AI panel' : 'Detach AI panel'}
                onClick={() => setAiDetached(value => !value)}
              >
                {detached ? <AttachIcon /> : <DetachIcon />}
              </button>
              <button
                type="button"
                title="Collapse AI"
                aria-label="Collapse AI"
                onClick={() => {
                  setAiExpanded(false);
                  setAiDetached(false);
                }}
              >
                <CollapseIcon />
              </button>
            </div>
          </div>
          {aiMessages.length > 0 && (
            <div className={styles.aiMessages}>
              {aiMessages.map((message, index) => (
                <div
                  key={`${message.role}-${index}`}
                  className={`${styles.aiMessage} ${message.role === 'user' ? styles.aiMessageUser : styles.aiMessageAssistant}`}
                >
                  {editingAIMessageIndex === index ? (
                    <div className={styles.aiMessageEditor}>
                      <textarea
                        value={editingAIMessageText}
                        onChange={event => setEditingAIMessageText(event.target.value)}
                        onKeyDown={event => handleAIEditKeyDown(event, index)}
                        rows={3}
                      />
                      <div className={styles.aiMessageEditorActions}>
                        <button type="button" onClick={() => handleAIEditResend(index)} disabled={aiLoading || !editingAIMessageText.trim()}>
                          <ResendIcon />
                          <span>Resend</span>
                        </button>
                        <button type="button" onClick={handleAIEditCancel}>Cancel</button>
                      </div>
                    </div>
                  ) : (
                    <>
                      <div className={styles.aiMessageText}>{message.content}</div>
                      <div className={styles.aiMessageActions}>
                        <button type="button" title="Copy" aria-label="Copy message" onClick={() => handleAIMessageAction('copy', message, index)}><CopyIcon /></button>
                        {message.role === 'user' && (
                          <>
                            <button type="button" title="Edit" aria-label="Edit message" onClick={() => handleAIMessageAction('edit', message, index)}><EditIcon /></button>
                            <button type="button" title="Resend" aria-label="Resend message" onClick={() => handleAIMessageAction('resend', message, index)} disabled={aiLoading}><ResendIcon /></button>
                            <button type="button" title="Delete" aria-label="Delete message" onClick={() => handleAIMessageAction('delete', message, index)}><DeleteIcon /></button>
                          </>
                        )}
                      </div>
                    </>
                  )}
                </div>
              ))}
              {aiLoading && (
                <div className={`${styles.aiMessage} ${styles.aiMessageAssistant}`}>
                  <div className={styles.aiMessageText}>{AI_LOADING_MESSAGES[aiLoadingMessageIndex]}</div>
                </div>
              )}
            </div>
          )}
          {aiSplashVisible && aiMessages.length === 0 && (
            <div className={styles.aiSplash} aria-hidden="true">
              <div className={styles.aiSplashMark}>
                <AISparkleIcon />
              </div>
              <span>ANTARIS INTELLIGENCE</span>
            </div>
          )}
          {aiMessages.length === 0 && !aiLoading && (
            <div className={styles.aiEmptyState}>
              <div className={styles.aiEmptyOrb} aria-hidden="true">
                <AISparkleIcon />
              </div>
              <span className={styles.aiLogoLabelText}>ANTARIS INTELLIGENCE</span>
              <div className={styles.aiOptionRow}>
                {[
                  'Give me an executive summary',
                  'Find highest value access windows',
                  'Show coverage gaps and risks',
                  'Compare satellites against ground stations',
                ].map(option => (
                  <button
                    key={option}
                    type="button"
                    onClick={() => setAiPrompt(option)}
                  >
                    {option}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </section>
  );

  const handleSelectCoverageReport = async (report: CoverageAnalysisReport) => {
    setLoadingReportId(report.reportId);
    try {
      const res = await MissionService.getCoverageAnalysisReportById(mission_id, scenario_id, report.reportId);
      const data = res?.data?.data ?? res?.data;

      const fullReport = {
        ...report,
        name: data?.reportName ?? data?.name ?? data?.report_name ?? report.name,
        status: String(data?.status ?? report.status).toLowerCase(),
        raw: data,
      };

      upsertCoverageAnalysisReport(fullReport);
      setSelectedCoverageReportId(report.reportId);
      setSelectedCoverageReportDetail(fullReport);

      const aois = data?.requestInput?.aoiobjects ?? [];
      if (aois.length > 0) {
        setSelectedAoiId(aois[0].aoiId);
      } else {
        setSelectedAoiId(null);
      }
      setCoveragePage(0);
      setSimpleCoverageData(null);
      setGapSummaryData(null);
      setRevisitSummaryData(null);
    } catch (error) {
      showToastMessage('error', 'Failed to fetch coverage report details');
    } finally {
      setLoadingReportId(null);
    }
  };

  const handleRenameCoverage = async (reportId: string) => {
    if (!mission_id || !scenario_id || !renamingName.trim()) return;
    try {
      await MissionService.renameCoverageAnalysisReport(mission_id, scenario_id, reportId, renamingName.trim());
      showToastMessage('success', 'Report renamed successfully');

      upsertCoverageAnalysisReport({
        reportId,
        name: renamingName.trim(),
        status: selectedCoverageReportDetail?.reportId === reportId ? selectedCoverageReportDetail.status : 'completed'
      });

      if (selectedCoverageReportDetail && selectedCoverageReportDetail.reportId === reportId) {
        setSelectedCoverageReportDetail({ ...selectedCoverageReportDetail, name: renamingName.trim() });
      }
      setRenamingCoverageReportId(null);
    } catch (err) {
      showToastMessage('error', 'Failed to rename report');
    }
  };

  const handleDeleteCoverageReport = async (reportId: string, reportName?: string) => {
    if (!mission_id || !scenario_id) return;
    setDeletingReportId(reportId);
    try {
      await MissionService.deleteCoverageAnalysisReport(mission_id, scenario_id, reportId);
      const displayName = reportName ? ` ${reportName}` : '';
      showToastMessage('success', `Coverage Report${displayName} deleted`);

      setCoverageAnalysisReports(coverageAnalysisReports.filter(r => r.reportId !== reportId));
      if (selectedCoverageReportId === reportId) {
        setSelectedCoverageReportId(null);
        setSelectedCoverageReportDetail(null);
      }
    } catch (err) {
      showToastMessage('error', 'Failed to delete report');
    } finally {
      setDeletingReportId(null);
      setPendingDeleteReport(null);
    }
  };

  const handleConfirmDeleteReport = async () => {
    if (!pendingDeleteReport) return;
    const { id, name, type } = pendingDeleteReport;
    if (type === 'access') {
      await handleDeleteReport(id, name);
    } else {
      await handleDeleteCoverageReport(id, name);
    }
  };

  useEffect(() => {
    if (!selectedCoverageReportId || !selectedAoiId || reportsTab !== 'coverage') return;

    const currentCoverageKey = `${selectedCoverageReportId}_${selectedAoiId}_${coverageSectionTab}_${coveragePage}_${selectedSatFilter}`;
    const optionsChanged = lastFetchedCoverageKeyRef.current !== currentCoverageKey;

    const hasData = !optionsChanged && (
      coverageSectionTab === 'simple'
        ? simpleCoverageData !== null
        : coverageSectionTab === 'gap'
          ? gapSummaryData !== null
          : revisitSummaryData !== null
    );

    if (hasData) {
      // Data is already cached for these exact options, do not fetch again
      return;
    }

    lastFetchedCoverageKeyRef.current = currentCoverageKey;

    let active = true;
    const loadSection = async () => {
      setCoverageLoading(true);
      setCoverageError(null);
      if (optionsChanged) {
        if (coverageSectionTab === 'simple') {
          setSimpleCoverageData(null);
          setCoverageVisualizationPasses(null);
        } else if (coverageSectionTab === 'gap') {
          setGapSummaryData(null);
          setGapVisualizationSpots(null);
        } else if (coverageSectionTab === 'revisit') {
          setRevisitSummaryData(null);
        }
      }
      try {
        const opts = {
          satelliteIds: selectedSatFilter,
          page: coveragePage,
          size: 20
        };

        if (coverageSectionTab === 'simple') {
          // Fetch simple coverage summary
          const simpleRes = await MissionService.getCoverageSimpleCoverage(mission_id, scenario_id, selectedCoverageReportId, selectedAoiId, opts);
          if (active) {
            setSimpleCoverageData(simpleRes?.data?.data ?? simpleRes?.data);
          }
          // Fetch simple passes for visualization asynchronously in background (non-blocking)
          MissionService.getCoverageAnalysisPasses(mission_id, scenario_id, selectedCoverageReportId, selectedAoiId, {
            satelliteIds: selectedSatFilter || '',
            page: 0,
            size: 1000,
          })
            .then((passesRes) => {
              if (active && passesRes) {
                setCoverageVisualizationPasses(passesRes?.data?.data ?? passesRes?.data);
              }
            })
            .catch(() => null);

        } else if (coverageSectionTab === 'gap') {
          // Fetch gap summary
          const gapRes = await MissionService.getCoverageGapSummary(mission_id, scenario_id, selectedCoverageReportId, selectedAoiId, opts);
          if (active) {
            setGapSummaryData(gapRes?.data?.data ?? gapRes?.data);
          }
          // Fetch spots for visualization asynchronously in background (non-blocking)
          MissionService.getCoverageAnalysisSpots(mission_id, scenario_id, selectedCoverageReportId, selectedAoiId, {
            satelliteIds: selectedSatFilter || '',
            page: 0,
            size: 1000,
          })
            .then((spotsRes) => {
              if (active && spotsRes) {
                setGapVisualizationSpots(spotsRes?.data?.data ?? spotsRes?.data);
              }
            })
            .catch(() => null);

        } else if (coverageSectionTab === 'revisit') {
          const revisitRes = await MissionService.getCoverageRevisitSummary(mission_id, scenario_id, selectedCoverageReportId, selectedAoiId, opts);
          if (active) {
            setRevisitSummaryData(revisitRes?.data?.data ?? revisitRes?.data);
          }
          MissionService.getCoverageAnalysisSpots(mission_id, scenario_id, selectedCoverageReportId, selectedAoiId, {
            satelliteIds: selectedSatFilter || '',
            page: 0,
            size: 1000,
          })
            .then((spotsRes) => {
              if (active && spotsRes) {
                setGapVisualizationSpots(spotsRes?.data?.data ?? spotsRes?.data);
              }
            })
            .catch(() => null);
        }
      } catch (err) {
        if (active) {
          const message = (err as any)?.response?.data?.message ?? (err as Error)?.message ?? 'Failed to load coverage data';
          setCoverageError(message);
          showToastMessage('error', message);
          setCoverageError(message);
        }
      } finally {
        if (active) {
          setCoverageLoading(false);
        }
      }
    };

    void loadSection();
    return () => {
      active = false;
    };
  }, [
    selectedCoverageReportId,
    selectedAoiId,
    coverageSectionTab,
    coveragePage,
    selectedSatFilter,
    reportsTab,
    mission_id,
    scenario_id
  ]);

  useEffect(() => {
    if (reportsTab !== 'coverage' || !selectedCoverageReportId) {
      cesium.clearCoverageVisualization?.();
      return;
    }

    const durationHrs = selectedCoverageReportDetail?.raw?.requestInput?.duration ??
      selectedCoverageReportDetail?.raw?.requestInput?.durationHrs ??
      selectedCoverageReportDetail?.raw?.requestInput?.durationHours ??
      24;
    const totalDurationMin = durationHrs * 60;

    if (coverageSectionTab === 'simple' && coverageVisualizationPasses) {
      const items = getList(coverageVisualizationPasses);
      if (items.length > 0) {
        cesium.renderCoveragePasses?.(items, false, heatmapMetricMode, totalDurationMin);
      } else {
        cesium.clearCoverageVisualization?.();
      }
    } else if (coverageSectionTab === 'gap' && gapVisualizationSpots) {
      const items = getList(gapVisualizationSpots);
      if (items.length > 0) {
        cesium.renderCoveragePasses?.(items, true, heatmapMetricMode, totalDurationMin);
      } else {
        cesium.clearCoverageVisualization?.();
      }
    } else if (coverageSectionTab === 'revisit' && gapVisualizationSpots) {
      const items = getList(gapVisualizationSpots);
      if (items.length > 0) {
        cesium.renderCoveragePasses?.(
          items,
          true,
          heatmapMetricMode,
          totalDurationMin,
          heatmapMetricMode === 'count' ? revisitThresholds : undefined
        );
      } else {
        cesium.clearCoverageVisualization?.();
      }
    } else {
      cesium.clearCoverageVisualization?.();
    }

    return () => {
      cesium.clearCoverageVisualization?.();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reportsTab, selectedCoverageReportId, coverageSectionTab, coverageVisualizationPasses, gapVisualizationSpots, heatmapMetricMode, selectedCoverageReportDetail, revisitThresholds]);

  const getList = (obj: any): any[] => {
    if (!obj) return [];
    if (Array.isArray(obj)) return obj;
    if (Array.isArray(obj.items)) return obj.items;
    if (Array.isArray(obj.content)) return obj.content;
    if (Array.isArray(obj.passes)) return obj.passes;
    if (Array.isArray(obj.spots)) return obj.spots;
    if (obj.passesPage && Array.isArray(obj.passesPage.items)) return obj.passesPage.items;
    if (obj.spotsPage && Array.isArray(obj.spotsPage.items)) return obj.spotsPage.items;
    return [];
  };

  const dynamicBins = useMemo(() => {
    const spots = getList(gapVisualizationSpots || coverageVisualizationPasses);
    if (!spots || spots.length === 0) return [];

    let minVal = Infinity;
    let maxVal = -Infinity;

    spots.forEach((spot: any) => {
      let v = 0;
      if (heatmapMetricMode === 'count') {
        v = spot.revisit_count ?? spot.revisits ?? spot.count ?? 0;
      } else if (heatmapMetricMode === 'max') {
        v = spot.mission_max_gap_seconds ?? spot.max_gap_seconds ?? 0;
      } else if (heatmapMetricMode === 'avg') {
        v = spot.mission_avg_gap_seconds ?? spot.avg_gap_seconds ?? 0;
      } else if (heatmapMetricMode === 'min') {
        v = spot.mission_min_gap_seconds ?? spot.min_gap_seconds ?? 0;
      }
      if (typeof v === 'number' && Number.isFinite(v)) {
        if (v < minVal) minVal = v;
        if (v > maxVal) maxVal = v;
      }
    });

    if (!Number.isFinite(minVal)) minVal = 0;
    if (!Number.isFinite(maxVal) || maxVal <= minVal) maxVal = minVal + 300;

    const step = (maxVal - minVal) / 5;
    const b1 = minVal + step;
    const b2 = minVal + 2 * step;
    const b3 = minVal + 3 * step;
    const b4 = minVal + 4 * step;

    const fmt = (val: number) => {
      if (heatmapMetricMode === 'count') {
        return String(Math.round(val));
      }
      if (val < 120) return `${Math.round(val)}s`;
      if (val < 7200) return `${(val / 60).toFixed(1)}m`;
      return `${(val / 3600).toFixed(1)}h`;
    };

    const isGap = coverageSectionTab === 'gap';
    const isRevisitDuration = coverageSectionTab === 'revisit' && heatmapMetricMode !== 'count';
    const isLargerBad = isGap || isRevisitDuration;

    if (revisitThresholds && revisitThresholds.length === 4) {
      const [t1, t2, t3, t4] = revisitThresholds;
      return [
        { color: isLargerBad ? '#22c55e' : '#ef4444', label: `< ${fmt(t1)}` },
        { color: isLargerBad ? '#84cc16' : '#f97316', label: `${fmt(t1)} - ${fmt(t2)}` },
        { color: isLargerBad ? '#eab308' : '#eab308', label: `${fmt(t2)} - ${fmt(t3)}` },
        { color: isLargerBad ? '#f97316' : '#84cc16', label: `${fmt(t3)} - ${fmt(t4)}` },
        { color: isLargerBad ? '#ef4444' : '#22c55e', label: `> ${fmt(t4)}` },
      ];
    }

    return [
      { color: isLargerBad ? '#22c55e' : '#ef4444', label: `< ${fmt(b1)}` },
      { color: isLargerBad ? '#84cc16' : '#f97316', label: `${fmt(b1)} - ${fmt(b2)}` },
      { color: isLargerBad ? '#eab308' : '#eab308', label: `${fmt(b2)} - ${fmt(b3)}` },
      { color: isLargerBad ? '#f97316' : '#84cc16', label: `${fmt(b3)} - ${fmt(b4)}` },
      { color: isLargerBad ? '#ef4444' : '#22c55e', label: `> ${fmt(b4)}` },
    ];
  }, [gapVisualizationSpots, coverageVisualizationPasses, heatmapMetricMode, coverageSectionTab, revisitThresholds]);

  const renderCoverageTable = () => {
    if (coverageError) {
      return <div style={{ padding: '20px 0', textAlign: 'center', opacity: 0.8, color: '#f87171', fontSize: 12, fontFamily: 'Montserrat, sans-serif', textTransform: 'capitalize' }}>{coverageError}</div>;
    }
    if (coverageLoading) return <div style={{ padding: '20px 0', textAlign: 'center', opacity: 0.6, fontSize: 12, fontFamily: 'Montserrat, sans-serif' }}>Loading section data...</div>;

    if (coverageSectionTab === 'simple') {
      const passes = getList(coverageVisualizationPasses);
      if (coverageVisualizationPasses === null) {
        return <div style={{ padding: '20px 0', textAlign: 'center', opacity: 0.6, fontSize: 12, fontFamily: 'Montserrat, sans-serif' }}>{coverageLoading ? 'Loading coverage windows...' : 'No coverage windows recorded'}</div>;
      }
      if (passes.length === 0) {
        return <div style={{ padding: '20px 0', textAlign: 'center', opacity: 0.6, fontSize: 12, fontFamily: 'Montserrat, sans-serif' }}>No coverage windows recorded</div>;
      }

      return (
        <div className={styles.figmaTableContainer}>
          <table className={styles.figmaTable}>
            <thead>
              <tr>
                <th>#</th>
                <th>START TIME</th>
                <th>END TIME</th>
                <th>WINDOW DURATION</th>
                <th>AREA COVERED (%)</th>
              </tr>
            </thead>
            <tbody>
              {passes.map((w: any, idx: number) => {
                const rawStart = w.start_ts ?? w.start_utc ?? w.start ?? w.startTime;
                const rawEnd = w.end_ts ?? w.end_utc ?? w.end ?? w.endTime;
                const startEpoch = toEpoch(rawStart);
                const endEpoch = toEpoch(rawEnd);
                const isOngoing = currentEpoch != null && startEpoch != null && endEpoch != null &&
                  currentEpoch >= startEpoch && currentEpoch <= endEpoch;

                let startStr = '-';
                if (rawStart) {
                  const d = new Date(typeof rawStart === 'number' ? (rawStart > 10_000_000_000 ? rawStart : rawStart * 1000) : rawStart);
                  startStr = !Number.isNaN(d.getTime())
                    ? d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false, timeZone: 'UTC' })
                    : String(rawStart).replace(/.*at\s*/, '');
                }

                let endStr = '-';
                if (rawEnd) {
                  const d = new Date(typeof rawEnd === 'number' ? (rawEnd > 10_000_000_000 ? rawEnd : rawEnd * 1000) : rawEnd);
                  endStr = !Number.isNaN(d.getTime())
                    ? d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false, timeZone: 'UTC' })
                    : String(rawEnd).replace(/.*at\s*/, '');
                }

                let durSec = w.duration_seconds ?? w.durationSeconds ?? w.duration;
                if (durSec == null && typeof rawStart === 'number' && typeof rawEnd === 'number') {
                  durSec = rawEnd - rawStart;
                }

                let durFormatted = '-';
                if (typeof durSec === 'number') {
                  if (durSec >= 3600) {
                    const h = Math.floor(durSec / 3600);
                    const m = Math.round((durSec % 3600) / 60);
                    durFormatted = m > 0 ? `${h}H ${m}m` : `${h}H`;
                  } else if (durSec >= 60) {
                    const m = Math.floor(durSec / 60);
                    const s = Math.round(durSec % 60);
                    durFormatted = s > 0 ? `${m}m ${s}s` : `${m}m`;
                  } else {
                    durFormatted = `${Math.round(durSec)}s`;
                  }
                } else if (durSec) {
                  durFormatted = String(durSec);
                }

                let percentageVal = w.percentage ?? w.pct ?? w.coverage_pct ?? w.coverage_percentage ?? w.coveragePercentage ?? w.percent;
                let percentageFormatted = '-';
                if (percentageVal != null) {
                  const num = Number(percentageVal);
                  if (!Number.isNaN(num)) {
                    percentageFormatted = `${num.toFixed(2)}%`;
                  } else {
                    percentageFormatted = String(percentageVal);
                  }
                }

                return (
                  <tr key={idx} className={isOngoing ? styles.ongoingRow : ''}>
                    <td>{String(idx + 1).padStart(2, '0')}</td>
                    <td>{startStr}</td>
                    <td>{endStr}</td>
                    <td>{durFormatted}</td>
                    <td>{percentageFormatted}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      );
    }

    const currentData = coverageSectionTab === 'gap'
      ? gapSummaryData
      : revisitSummaryData;

    if (!currentData) return <div style={{ padding: '20px 0', textAlign: 'center', opacity: 0.6, fontSize: 12, fontFamily: 'Montserrat, sans-serif' }}>No data available</div>;

    if (coverageSectionTab === 'revisit') {
      const spots = getList(gapVisualizationSpots);
      if (gapVisualizationSpots === null) {
        return <div style={{ padding: '20px 0', textAlign: 'center', opacity: 0.6, fontSize: 12, fontFamily: 'Montserrat, sans-serif' }}>Loading grid spots...</div>;
      }
      if (!Array.isArray(spots) || spots.length === 0) {
        return <div style={{ padding: '20px 0', textAlign: 'center', opacity: 0.6, fontSize: 12, fontFamily: 'Montserrat, sans-serif' }}>No grid spots recorded</div>;
      }

      return (
        <div className={styles.figmaTableContainer}>
          <table className={styles.figmaTable}>
            <thead>
              <tr>
                <th>#</th>
                <th>Spot ID</th>
                <th>Latitude (°)</th>
                <th>Longitude (°)</th>
                <th>Revisits</th>
                <th>Max Gap</th>
                <th>Avg Gap</th>
                <th>Min Gap</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {spots.map((spot: any, idx: number) => {
                const lat = spot.lla?.lat ?? spot.lat;
                const lng = spot.lla?.lng ?? spot.lng ?? spot.lla?.lon ?? spot.lon;
                const revisitCount = spot.revisit_count ?? spot.revisits ?? spot.count ?? 0;

                const maxGapSec = spot.mission_max_gap_seconds ?? spot.max_gap_seconds;
                const avgGapSec = spot.mission_avg_gap_seconds ?? spot.avg_gap_seconds;
                const minGapSec = spot.mission_min_gap_seconds ?? spot.min_gap_seconds;

                const formatSecVal = (val: any) => {
                  if (val === null || val === undefined) return '-';
                  const sec = Number(val);
                  if (Number.isNaN(sec)) return '-';
                  if (sec >= 3600) {
                    const h = Math.floor(sec / 3600);
                    const m = Math.round((sec % 3600) / 60);
                    return m > 0 ? `${h}h ${m}m` : `${h}h`;
                  }
                  if (sec >= 60) {
                    const m = Math.floor(sec / 60);
                    const s = Math.round(sec % 60);
                    return s > 0 ? `${m}m ${s}s` : `${m}m`;
                  }
                  return `${Math.round(sec)}s`;
                };

                const maxGap = formatSecVal(maxGapSec);
                const avgGap = formatSecVal(avgGapSec);
                const minGap = formatSecVal(minGapSec);

                const status = spot.revisit_gap_status || (revisitCount > 0 ? 'covered' : 'uncovered');
                const isInsufficient = status === 'insufficient_revisits';

                return (
                  <tr
                    key={idx}
                    style={{ cursor: Number.isFinite(lat) && Number.isFinite(lng) ? 'pointer' : 'default' }}
                    onClick={() => {
                      if (Number.isFinite(lat) && Number.isFinite(lng)) {
                        cesium.centerOnPoint?.(lng, lat);
                      }
                    }}
                    title="Click to locate on map"
                  >
                    <td>{idx + 1}</td>
                    <td><span style={{ color: '#ccf54e', fontWeight: 600 }}>{spot.id || `ST${idx}`}</span></td>
                    <td>{typeof lat === 'number' ? lat.toFixed(4) : '-'}</td>
                    <td>{typeof lng === 'number' ? lng.toFixed(4) : '-'}</td>
                    <td>{revisitCount}</td>
                    <td>{maxGap}</td>
                    <td>{avgGap}</td>
                    <td>{minGap}</td>
                    <td>
                      <span style={{
                        fontSize: 10,
                        padding: '1px 6px',
                        borderRadius: 3,
                        background: isInsufficient || status.includes('uncovered') ? 'rgba(255, 77, 77, 0.15)' : 'rgba(204, 245, 78, 0.15)',
                        color: isInsufficient || status.includes('uncovered') ? '#ff4d4d' : '#ccf54e',
                        border: `0.5px solid ${isInsufficient || status.includes('uncovered') ? 'rgba(255, 77, 77, 0.3)' : 'rgba(204, 245, 78, 0.3)'}`,
                      }}>
                        {status.replace(/_/g, ' ')}
                      </span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      );
    }

    if (coverageSectionTab === 'gap') {
      if (gapTableMode === 'spots') {
        const spots = getList(gapVisualizationSpots);
        if (gapVisualizationSpots === null) {
          return <div style={{ padding: '20px 0', textAlign: 'center', opacity: 0.6, fontSize: 12, fontFamily: 'Montserrat, sans-serif' }}>Loading grid spots...</div>;
        }
        if (!Array.isArray(spots) || spots.length === 0) {
          return <div style={{ padding: '20px 0', textAlign: 'center', opacity: 0.6, fontSize: 12, fontFamily: 'Montserrat, sans-serif' }}>No grid spots recorded</div>;
        }

        return (
          <div className={styles.figmaTableContainer}>
            <table className={styles.figmaTable}>
              <thead>
                <tr>
                  <th>#</th>
                  <th>Spot ID</th>
                  <th>Latitude (°)</th>
                  <th>Longitude (°)</th>
                  <th>Revisits</th>
                  <th>Max Gap (min)</th>
                  <th>Avg Gap (min)</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {spots.map((spot: any, idx: number) => {
                  const lat = spot.lla?.lat ?? spot.lat;
                  const lng = spot.lla?.lng ?? spot.lng ?? spot.lla?.lon ?? spot.lon;
                  const maxGapSec = spot.mission_max_gap_seconds ?? spot.max_gap_seconds;
                  const avgGapSec = spot.mission_avg_gap_seconds ?? spot.avg_gap_seconds;
                  const maxGapMin = typeof maxGapSec === 'number' ? (maxGapSec / 60).toFixed(1) : '-';
                  const avgGapMin = typeof avgGapSec === 'number' ? (avgGapSec / 60).toFixed(1) : '-';
                  const status = spot.revisit_gap_status || (spot.revisit_count > 0 ? 'covered' : 'uncovered');

                  return (
                    <tr
                      key={idx}
                      style={{ cursor: Number.isFinite(lat) && Number.isFinite(lng) ? 'pointer' : 'default' }}
                      onClick={() => {
                        if (Number.isFinite(lat) && Number.isFinite(lng)) {
                          cesium.centerOnPoint?.(lng, lat);
                        }
                      }}
                      title="Click to locate on map"
                    >
                      <td>{idx + 1}</td>
                      <td><span style={{ color: '#ccf54e', fontWeight: 600 }}>{spot.id || `ST${idx}`}</span></td>
                      <td>{typeof lat === 'number' ? lat.toFixed(4) : '-'}</td>
                      <td>{typeof lng === 'number' ? lng.toFixed(4) : '-'}</td>
                      <td>{spot.revisit_count ?? 0}</td>
                      <td>{maxGapMin}</td>
                      <td>{avgGapMin}</td>
                      <td>
                        <span style={{
                          fontSize: 10,
                          padding: '1px 6px',
                          borderRadius: 3,
                          background: status.includes('insufficient') || status.includes('uncovered') ? 'rgba(255, 77, 77, 0.15)' : 'rgba(204, 245, 78, 0.15)',
                          color: status.includes('insufficient') || status.includes('uncovered') ? '#ff4d4d' : '#ccf54e',
                          border: `0.5px solid ${status.includes('insufficient') || status.includes('uncovered') ? 'rgba(255, 77, 77, 0.3)' : 'rgba(204, 245, 78, 0.3)'}`,
                        }}>
                          {status}
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        );
      }

      const gapSummary = currentData?.gapSummary ?? currentData?.data?.gapSummary ?? currentData;
      const windows = gapSummary?.gap_windows ?? gapSummary?.gapWindows ?? [];

      if (!Array.isArray(windows) || windows.length === 0) {
        return <div style={{ padding: '20px 0', textAlign: 'center', opacity: 0.6, fontSize: 12, fontFamily: 'Montserrat, sans-serif' }}>No gap windows recorded</div>;
      }

      return (
        <div className={styles.figmaTableContainer}>
          <table className={styles.figmaTable}>
            <thead>
              <tr>
                <th>#</th>
                <th>START TIME</th>
                <th>END TIME</th>
                <th>GAP DURATION</th>
              </tr>
            </thead>
            <tbody>
              {windows.map((w: any, idx: number) => {
                const rawStart = w.start_ts ?? w.start_utc ?? w.start ?? w.startTime;
                const rawEnd = w.end_ts ?? w.end_utc ?? w.end ?? w.endTime;
                const startEpoch = toEpoch(rawStart);
                const endEpoch = toEpoch(rawEnd);
                const isOngoing = currentEpoch != null && startEpoch != null && endEpoch != null &&
                  currentEpoch >= startEpoch && currentEpoch <= endEpoch;

                let startStr = '-';
                if (rawStart) {
                  const d = new Date(typeof rawStart === 'number' ? (rawStart > 10_000_000_000 ? rawStart : rawStart * 1000) : rawStart);
                  startStr = !Number.isNaN(d.getTime())
                    ? d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false, timeZone: 'UTC' })
                    : String(rawStart).replace(/.*at\s*/, '');
                }

                let endStr = '-';
                if (rawEnd) {
                  const d = new Date(typeof rawEnd === 'number' ? (rawEnd > 10_000_000_000 ? rawEnd : rawEnd * 1000) : rawEnd);
                  endStr = !Number.isNaN(d.getTime())
                    ? d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false, timeZone: 'UTC' })
                    : String(rawEnd).replace(/.*at\s*/, '');
                }

                let durSec = w.duration_seconds ?? w.durationSeconds ?? w.duration;
                if (durSec == null && typeof w.duration_min === 'number') durSec = w.duration_min * 60;
                if (durSec == null && typeof w['Duration (sec)'] === 'number') durSec = w['Duration (sec)'];
                if (durSec == null && typeof w['Duration (min)'] === 'number') durSec = w['Duration (min)'] * 60;

                let durFormatted = '-';
                if (typeof durSec === 'number') {
                  if (durSec >= 3600) {
                    const h = Math.floor(durSec / 3600);
                    const m = Math.round((durSec % 3600) / 60);
                    durFormatted = m > 0 ? `${h}H ${m}m` : `${h}H`;
                  } else if (durSec >= 60) {
                    const m = Math.floor(durSec / 60);
                    const s = Math.round(durSec % 60);
                    durFormatted = s > 0 ? `${m}m ${s}s` : `${m}m`;
                  } else {
                    durFormatted = `${Math.round(durSec)}s`;
                  }
                } else if (durSec) {
                  durFormatted = String(durSec);
                }

                return (
                  <tr key={idx} className={isOngoing ? styles.ongoingRow : ''}>
                    <td>{String(idx + 1).padStart(2, '0')}</td>
                    <td>{startStr}</td>
                    <td>{endStr}</td>
                    <td>{durFormatted}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      );
    }

    const items = getList(currentData);
    if (items.length === 0) return <div style={{ padding: '20px 0', textAlign: 'center', opacity: 0.6, fontSize: 12, fontFamily: 'Montserrat, sans-serif' }}>No records found</div>;

    const firstItem = items[0];
    const keys = Object.keys(firstItem).filter(k => typeof firstItem[k] !== 'object' && k !== 'raw');

    const formatHeader = (key: string) => {
      return key
        .replace(/([A-Z])/g, ' $1')
        .replace(/^./, str => str.toUpperCase())
        .replace(/Utc/g, ' (UTC)')
        .replace(/Sec/g, ' (sec)')
        .replace(/Deg/g, ' (deg)')
        .replace(/Km/g, ' (km)');
    };

    return (
      <div className={styles.figmaTableContainer}>
        <table className={styles.figmaTable}>
          <thead>
            <tr>
              {keys.map(key => (
                <th key={key}>{formatHeader(key)}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {items.map((item, idx) => (
              <tr key={idx}>
                {keys.map(key => {
                  const val = item[key];
                  let displayed = '';
                  if (val == null) displayed = '-';
                  else if (typeof val === 'number') {
                    if (key.toLowerCase().includes('time') || key.toLowerCase().includes('epoch')) {
                      displayed = formatReportDate(val);
                    } else {
                      displayed = Number.isInteger(val) ? String(val) : val.toFixed(2);
                    }
                  } else displayed = String(val);

                  return <td key={key}>{displayed}</td>;
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  };

  const renderCoveragePagination = () => {
    const currentData = coverageSectionTab === 'simple'
      ? simpleCoverageData
      : (coverageSectionTab === 'gap' && gapTableMode === 'windows')
        ? gapSummaryData
        : gapVisualizationSpots;

    if (!currentData) return null;
    const items = getList(currentData);
    const hasNext = currentData.hasNext ?? (items.length === 20);
    const hasPrev = coveragePage > 0;

    if (!hasNext && !hasPrev) return null;

    return (
      <div className={styles.paginationContainer}>
        <button
          className={styles.paginationBtn}
          type="button"
          disabled={!hasPrev}
          onClick={() => setCoveragePage(p => p - 1)}
        >
          Previous
        </button>
        <span style={{ fontSize: 11, opacity: 0.7, fontFamily: 'Montserrat, sans-serif' }}>Page {coveragePage + 1}</span>
        <button
          className={styles.paginationBtn}
          type="button"
          disabled={!hasNext}
          onClick={() => setCoveragePage(p => p + 1)}
        >
          Next
        </button>
      </div>
    );
  };

  const renderKPIs = () => {
    const currentData = coverageSectionTab === 'simple'
      ? simpleCoverageData
      : coverageSectionTab === 'gap'
        ? gapSummaryData
        : revisitSummaryData;

    if (!currentData) return null;

    const simple = simpleCoverageData?.simpleCoverage ?? simpleCoverageData?.simple_coverage ?? simpleCoverageData;
    const gap = gapSummaryData?.gapAnalysis ?? gapSummaryData?.gap_analysis ?? gapSummaryData?.gapSummary ?? gapSummaryData?.gap_summary ?? gapSummaryData;
    const revisit = revisitSummaryData?.revisitAnalysis ?? revisitSummaryData?.revisit_analysis ?? revisitSummaryData?.revisitSummary ?? revisitSummaryData?.revisit_summary ?? revisitSummaryData;

    const pickVal = (source: any, keys: string[], fallback: string | number) => {
      if (!source) return fallback;
      for (const key of keys) {
        const val = source[key];
        if (val !== undefined && val !== null) {
          if (typeof val === 'number') return Number.isInteger(val) ? val : Number(val.toFixed(2));
          return val;
        }
      }
      return fallback;
    };

    let cards: { title: string; val: string | number; unit?: string }[] = [];

    if (coverageSectionTab === 'simple') {
      const src = simple ?? currentData;
      cards = [
        { title: 'COVERAGE %', val: pickVal(src, ['total_pct_covered', 'totalPctCovered', 'coveragePercent', 'coverage_percent'], 80), unit: '%' },
        { title: 'AREA COVERED', val: pickVal(src, ['area_covered_km2', 'areaCoveredKm2', 'areaCovered', 'area_covered'], 40), unit: 'km²' },
        { title: 'AREA UNCOVERED', val: pickVal(src, ['area_uncovered_km2', 'areaUncoveredKm2', 'areaUncovered', 'area_uncovered'], 19.20), unit: 'km²' },
        { title: 'EFFICIENCY', val: pickVal(src, ['efficiency', 'coverage_efficiency'], '-'), unit: '%' }
      ];
    } else if (coverageSectionTab === 'gap') {
      const src = gap ?? currentData;
      cards = [
        { title: 'SHORTEST GAP', val: pickVal(src, ['min_gap_seconds', 'minGapSeconds', 'min_gap', 'shortest_gap', 'shortestGap'], 0), unit: 's' },
        { title: 'LONGEST GAP', val: pickVal(src, ['max_gap_seconds', 'maxGapSeconds', 'max_gap', 'longest_gap', 'longestGap'], 0), unit: 's' },
        { title: 'MEAN GAP', val: pickVal(src, ['mean_gap_seconds', 'meanGapSeconds', 'mean_gap', 'averageGap'], 0), unit: 's' },
        { title: 'TOTAL GAP', val: pickVal(src, ['total_gap_seconds', 'totalGapSeconds', 'total_gap', 'totalGapDuration'], 0), unit: 's' }
      ];
    } else if (coverageSectionTab === 'revisit') {
      const src = revisitSummaryData?.revisitSummary ?? revisitSummaryData?.revisit_summary ?? revisitSummaryData ?? currentData;
      const formatSecVal = (val: any) => {
        if (val === null || val === undefined) return '-';
        const sec = Number(val);
        if (Number.isNaN(sec)) return '-';
        if (sec >= 3600) {
          const h = Math.floor(sec / 3600);
          const m = Math.round((sec % 3600) / 60);
          return m > 0 ? `${h}H ${m}m` : `${h}H`;
        }
        if (sec >= 60) {
          const m = Math.floor(sec / 60);
          const s = Math.round(sec % 60);
          return s > 0 ? `${m}m ${s}s` : `${m}m`;
        }
        return `${Math.round(sec)}s`;
      };
      cards = [
        { title: 'AVG REVISIT TIME', val: formatSecVal(pickVal(src, ['average_seconds', 'averageSeconds', 'avg_revisit_time', 'avgRevisitTime'], '-')) },
        { title: 'MAX REVISIT TIME', val: formatSecVal(pickVal(src, ['maximum_seconds', 'maximumSeconds', 'max_revisit_time', 'maxRevisitTime'], '-')) },
        { title: 'MIN REVISIT TIME', val: formatSecVal(pickVal(src, ['minimum_seconds', 'minimumSeconds', 'min_revisit_time', 'minRevisitTime'], '-')) },
        { title: 'TOTAL REVISITS', val: pickVal(src, ['total_revisits', 'totalRevisits', 'total_revisit_count'], '-') }
      ];
    }

    return (
      <div className={styles.figmaOverviewBlock}>
        <span className={styles.figmaOverviewTitle}>Overview</span>
        <div className={styles.figmaKpiRow}>
          {cards.map((c, i) => (
            <div key={i} className={styles.figmaKpiCard}>
              <span className={styles.figmaKpiLabel}>{c.title}</span>
              <div className={styles.figmaKpiValueSlot}>
                <span className={styles.figmaKpiValue}>{c.val}</span>
                {c.unit && <span className={styles.figmaKpiUnit}>{c.unit}</span>}
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  };

  const renderCoverageChart = () => {
    if (isTargetTrack && coverageSectionTab === 'gap') {
      return null;
    }

    if (coverageError) {
      const errorClass = coverageSectionTab === 'gap' ? styles.coverageChartErrorGap : styles.coverageChartError;
      return (
        <div className={errorClass}>
          <span className={styles.errorTitle}>Analysis Error</span>
          <span className={styles.errorSub}>{coverageError}</span>
        </div>
      );
    }

    const simple = simpleCoverageData?.simpleCoverage ?? simpleCoverageData?.simple_coverage ?? simpleCoverageData;
    const gapSummary = gapSummaryData?.gapSummary ?? gapSummaryData?.data?.gapSummary ?? gapSummaryData;
    const revisitData = revisitSummaryData?.revisitAnalysis ?? revisitSummaryData?.revisit_analysis ?? revisitSummaryData?.revisitSummary ?? revisitSummaryData?.revisit_summary ?? revisitSummaryData;

    const currentData = coverageSectionTab === 'simple'
      ? simple
      : coverageSectionTab === 'gap'
        ? gapSummary
        : revisitData;

    if (coverageError) {
      const containerHeight = coverageSectionTab === 'gap' ? 480 : 220;
      return (
        <div style={{ height: containerHeight, display: 'flex', alignItems: 'center', justifyContent: 'center', opacity: 0.8, color: '#f87171', fontSize: 12, fontFamily: 'Montserrat, sans-serif', textTransform: 'capitalize' }}>
          {coverageError}
        </div>
      );
    }

    if (!currentData) {
      // Immediate reserved container heights during load
      const containerHeight = coverageSectionTab === 'gap' ? 480 : 220;
      return (
        <div style={{ height: containerHeight, display: 'flex', alignItems: 'center', justifyContent: 'center', opacity: 0.4, fontSize: 11, fontFamily: 'Montserrat, sans-serif' }}>
          {coverageLoading ? 'Loading chart data...' : 'No chart data available'}
        </div>
      );
    }

    let chartTitle = '';
    let xAxisLabel = '';
    let yAxisLabel = '';
    let chartData: any[] = [];
    let lineKey = 'val';

    if (coverageSectionTab === 'simple') {
      chartTitle = 'Coverage Over Time';
      xAxisLabel = 'Time';
      yAxisLabel = '% Coverage';
      const series = simple?.cumulative_series ?? simple?.cumulativeSeries ?? [];
      if (!Array.isArray(series) || series.length === 0) {
        return null;
      }
      chartData = series.map((pt: any) => {
        const [ts, pct] = Array.isArray(pt) ? pt : [pt.time ?? pt.timestamp, pt.value ?? pt.pct];
        const date = new Date((ts > 10_000_000_000 ? ts : ts * 1000));
        const name = date.toLocaleTimeString('en-GB', {
          hour: '2-digit', minute: '2-digit', hour12: false,
          timeZone: 'UTC'
        });
        return { name, val: Number(Number(pct).toFixed(2)), epoch: toEpoch(ts) };
      });

      let closestPointName = '';
      if (currentEpoch != null && chartData.length > 0) {
        let minDiff = Infinity;
        chartData.forEach(pt => {
          if (pt.epoch != null) {
            const diff = Math.abs(pt.epoch - currentEpoch);
            if (diff < minDiff) {
              minDiff = diff;
              closestPointName = pt.name;
            }
          }
        });
      }

      return (
        <div className={styles.chartContainer}>
          <div className={styles.chartTitle}>{chartTitle}</div>
          <div style={{ width: '100%', height: 220 }}>
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={chartData} margin={{ top: 10, right: 10, left: 40, bottom: 25 }}>
                <defs>
                  <linearGradient id="simpleCoverageGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#ccf54e" stopOpacity={0.4} />
                    <stop offset="95%" stopColor="#ccf54e" stopOpacity={0.0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(240, 240, 240, 0.05)" />
                <XAxis
                  dataKey="name"
                  tick={{ fill: '#b4b4b4', fontSize: 9, fontFamily: 'Space Grotesk' }}
                  label={{ value: xAxisLabel, position: 'insideBottom', offset: -10, fill: '#b4b4b4', fontSize: 10, fontFamily: 'Space Grotesk', fontWeight: 500 }}
                  dy={5}
                />
                <YAxis
                  tick={{ fill: '#b4b4b4', fontSize: 9, fontFamily: 'Space Grotesk' }}
                  tickFormatter={(val) => `${val}%`}
                  label={{ value: yAxisLabel, angle: -90, position: 'insideLeft', offset: 0, style: { textAnchor: 'middle' }, fill: '#b4b4b4', fontSize: 10, fontFamily: 'Space Grotesk', fontWeight: 500 }}
                  dx={-5}
                />
                <Tooltip
                  contentStyle={{ background: '#090909', border: '0.5px solid #484848', borderRadius: 4, fontFamily: 'Montserrat' }}
                  labelStyle={{ color: '#eee', fontSize: 10 }}
                  itemStyle={{ color: '#ccf54e', fontSize: 10 }}
                  labelFormatter={(label) => `Time: ${label}`}
                  formatter={(value) => [`${value}%`, 'Coverage']}
                />
                <Legend
                  verticalAlign="top"
                  align="center"
                  iconType="circle"
                  iconSize={6}
                  wrapperStyle={{
                    fontSize: 10,
                    fontFamily: 'Space Grotesk',
                    color: '#b4b4b4',
                    paddingBottom: 15,
                  }}
                />
                <Area
                  type="monotone"
                  name="Area Covered"
                  dataKey={lineKey}
                  stroke="#ccf54e"
                  strokeWidth={1.5}
                  fill="url(#simpleCoverageGrad)"
                  dot={{ fill: '#ccf54e', r: 2 }}
                  activeDot={{ r: 4 }}
                />
                {closestPointName && closestPointName !== chartData[0]?.name && (
                  <ReferenceLine
                    x={closestPointName}
                    stroke="#ccf54e"
                    strokeDasharray="3 3"
                    label={{ value: 'NOW', position: 'top', fill: '#ccf54e', fontSize: 9, fontFamily: 'Space Grotesk' }}
                  />
                )}
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>
      );
    } else if (coverageSectionTab === 'gap') {
      const latData = Object.entries(gapSummary?.gap_lat_time_data || {})
        .map(([latStr, m]: [string, any]) => ({
          name: Number(Number(latStr).toFixed(1)),
          avgMin: Number(((m.avg_avg_time || 0) / 60).toFixed(1)),
          maxMin: Number(((m.max_max_time || 0) / 60).toFixed(1)),
          minMin: Number(((m.min_min_time || 0) / 60).toFixed(1)),
          stdMin: Number(((m.std_avg_time || 0) / 60).toFixed(1)),
        }))
        .sort((a, b) => a.name - b.name);

      const lonData = Object.entries(gapSummary?.gap_lon_time_data || {})
        .map(([lonStr, m]: [string, any]) => ({
          name: Number(Number(lonStr).toFixed(1)),
          avgMin: Number(((m.avg_avg_time || 0) / 60).toFixed(1)),
          maxMin: Number(((m.max_max_time || 0) / 60).toFixed(1)),
          minMin: Number(((m.min_min_time || 0) / 60).toFixed(1)),
          stdMin: Number(((m.std_avg_time || 0) / 60).toFixed(1)),
        }))
        .sort((a, b) => a.name - b.name);

      if (latData.length === 0 && lonData.length === 0) {
        return null;
      }

      return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16, padding: '0 12px 12px 12px' }}>
          {/* Chart 1: Lat Time Data */}
          <div className={styles.chartContainer}>
            <div className={styles.chartTitle}>Gap Duration vs Latitude</div>
            <div style={{ width: '100%', height: 255 }}>
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={latData} margin={{ top: 10, right: 10, left: 15, bottom: 30 }}>
                  <defs>
                    <linearGradient id="maxGapGradLat" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#ef4444" stopOpacity={0.06} />
                      <stop offset="95%" stopColor="#ef4444" stopOpacity={0.0} />
                    </linearGradient>
                    <linearGradient id="avgGapGradLat" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#eab308" stopOpacity={0.06} />
                      <stop offset="95%" stopColor="#eab308" stopOpacity={0.0} />
                    </linearGradient>
                    <linearGradient id="minGapGradLat" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#22c55e" stopOpacity={0.06} />
                      <stop offset="95%" stopColor="#22c55e" stopOpacity={0.0} />
                    </linearGradient>
                    <linearGradient id="stdGapGradLat" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#c084fc" stopOpacity={0.06} />
                      <stop offset="95%" stopColor="#c084fc" stopOpacity={0.0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="4 4" stroke="rgba(255, 255, 255, 0.05)" vertical={false} />
                  <XAxis
                    dataKey="name"
                    axisLine={false}
                    tickLine={false}
                    dy={5}
                    tick={{ fill: '#888', fontSize: 9, fontFamily: 'Space Grotesk' }}
                    label={{ value: 'Latitude (°)', position: 'insideBottom', offset: -15, fill: '#888', fontSize: 9, fontFamily: 'Space Grotesk' }}
                  />
                  <YAxis
                    axisLine={false}
                    tickLine={false}
                    dx={-5}
                    tick={{ fill: '#888', fontSize: 9, fontFamily: 'Space Grotesk' }}
                    label={{ value: 'Gap (min)', angle: -90, position: 'insideLeft', offset: 0, fill: '#888', fontSize: 9, fontFamily: 'Space Grotesk' }}
                  />
                  <Tooltip
                    contentStyle={{
                      background: 'rgba(15, 15, 15, 0.95)',
                      border: '1px solid rgba(255, 255, 255, 0.08)',
                      borderRadius: 6,
                      fontFamily: 'Space Grotesk',
                      boxShadow: '0 4px 20px rgba(0, 0, 0, 0.5)'
                    }}
                    labelStyle={{ color: '#ffffff', fontWeight: 600, fontSize: 10 }}
                    itemStyle={{ fontSize: 10, padding: '2px 0' }}
                    labelFormatter={(label) => `Latitude: ${label}°`}
                    formatter={(value, name) => [`${value} min`, name]}
                  />
                  <Legend
                    verticalAlign="top"
                    align="right"
                    iconType="circle"
                    iconSize={6}
                    wrapperStyle={{
                      fontSize: 10,
                      fontFamily: 'Space Grotesk',
                      color: '#b4b4b4',
                      paddingBottom: 15,
                      marginRight: 10
                    }}
                  />
                  <Area type="monotone" name="Max Gap (min)" dataKey="maxMin" stroke="#ef4444" strokeWidth={2} fill="url(#maxGapGradLat)" dot={false} activeDot={{ r: 4, strokeWidth: 0 }} />
                  <Area type="monotone" name="Avg Gap (min)" dataKey="avgMin" stroke="#eab308" strokeWidth={2} fill="url(#avgGapGradLat)" dot={false} activeDot={{ r: 4, strokeWidth: 0 }} />
                  <Area type="monotone" name="Min Gap (min)" dataKey="minMin" stroke="#22c55e" strokeWidth={2} fill="url(#minGapGradLat)" dot={false} activeDot={{ r: 4, strokeWidth: 0 }} />
                  <Area type="monotone" name="Std Dev (min)" dataKey="stdMin" stroke="#c084fc" strokeWidth={2} fill="url(#stdGapGradLat)" dot={false} activeDot={{ r: 4, strokeWidth: 0 }} />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* Chart 2: Lon Time Data */}
          <div className={styles.chartContainer}>
            <div className={styles.chartTitle}>Gap Duration vs Longitude</div>
            <div style={{ width: '100%', height: 255 }}>
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={lonData} margin={{ top: 10, right: 10, left: 15, bottom: 30 }}>
                  <defs>
                    <linearGradient id="maxGapGradLon" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#ef4444" stopOpacity={0.06} />
                      <stop offset="95%" stopColor="#ef4444" stopOpacity={0.0} />
                    </linearGradient>
                    <linearGradient id="avgGapGradLon" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#eab308" stopOpacity={0.06} />
                      <stop offset="95%" stopColor="#eab308" stopOpacity={0.0} />
                    </linearGradient>
                    <linearGradient id="minGapGradLon" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#22c55e" stopOpacity={0.06} />
                      <stop offset="95%" stopColor="#22c55e" stopOpacity={0.0} />
                    </linearGradient>
                    <linearGradient id="stdGapGradLon" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#c084fc" stopOpacity={0.06} />
                      <stop offset="95%" stopColor="#c084fc" stopOpacity={0.0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="4 4" stroke="rgba(255, 255, 255, 0.05)" vertical={false} />
                  <XAxis
                    dataKey="name"
                    axisLine={false}
                    tickLine={false}
                    dy={5}
                    tick={{ fill: '#888', fontSize: 9, fontFamily: 'Space Grotesk' }}
                    label={{ value: 'Longitude (°)', position: 'insideBottom', offset: -15, fill: '#888', fontSize: 9, fontFamily: 'Space Grotesk' }}
                  />
                  <YAxis
                    axisLine={false}
                    tickLine={false}
                    dx={-5}
                    tick={{ fill: '#888', fontSize: 9, fontFamily: 'Space Grotesk' }}
                    label={{ value: 'Gap (min)', angle: -90, position: 'insideLeft', offset: 0, fill: '#888', fontSize: 9, fontFamily: 'Space Grotesk' }}
                  />
                  <Tooltip
                    contentStyle={{
                      background: 'rgba(15, 15, 15, 0.95)',
                      border: '1px solid rgba(255, 255, 255, 0.08)',
                      borderRadius: 6,
                      fontFamily: 'Space Grotesk',
                      boxShadow: '0 4px 20px rgba(0, 0, 0, 0.5)'
                    }}
                    labelStyle={{ color: '#ffffff', fontWeight: 600, fontSize: 10 }}
                    itemStyle={{ fontSize: 10, padding: '2px 0' }}
                    labelFormatter={(label) => `Longitude: ${label}°`}
                    formatter={(value, name) => [`${value} min`, name]}
                  />
                  <Legend
                    verticalAlign="top"
                    align="right"
                    iconType="circle"
                    iconSize={6}
                    wrapperStyle={{
                      fontSize: 10,
                      fontFamily: 'Space Grotesk',
                      color: '#b4b4b4',
                      paddingBottom: 15,
                      marginRight: 10
                    }}
                  />
                  <Area type="monotone" name="Max Gap (min)" dataKey="maxMin" stroke="#ef4444" strokeWidth={2} fill="url(#maxGapGradLon)" dot={false} activeDot={{ r: 4, strokeWidth: 0 }} />
                  <Area type="monotone" name="Avg Gap (min)" dataKey="avgMin" stroke="#eab308" strokeWidth={2} fill="url(#avgGapGradLon)" dot={false} activeDot={{ r: 4, strokeWidth: 0 }} />
                  <Area type="monotone" name="Min Gap (min)" dataKey="minMin" stroke="#22c55e" strokeWidth={2} fill="url(#minGapGradLon)" dot={false} activeDot={{ r: 4, strokeWidth: 0 }} />
                  <Area type="monotone" name="Std Dev (min)" dataKey="stdMin" stroke="#c084fc" strokeWidth={2} fill="url(#stdGapGradLon)" dot={false} activeDot={{ r: 4, strokeWidth: 0 }} />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>
      );
    } else if (coverageSectionTab === 'revisit') {
      chartTitle = 'Revisit Count Over Time';
      xAxisLabel = 'Time';
      yAxisLabel = 'No of Revisits';

      const series = revisitSummaryData?.revisitSummary?.revisit_time_series ??
        revisitSummaryData?.revisit_summary?.revisit_time_series ??
        revisitSummaryData?.revisitSummary?.revisitTimeSeries ??
        revisitSummaryData?.revisit_time_series ??
        revisitData?.cumulative_series ??
        revisitData?.cumulativeSeries ??
        [];

      if (!Array.isArray(series) || series.length === 0) {
        const containerHeight = 200;
        return (
          <div style={{ height: containerHeight, display: 'flex', alignItems: 'center', justifyContent: 'center', opacity: 0.4, fontSize: 11, fontFamily: 'Montserrat, sans-serif' }}>
            {coverageLoading ? 'Loading chart data...' : 'No chart data available'}
          </div>
        );
      }

      chartData = series.map((pt: any) => {
        const ts = pt.timestamp ?? pt.time ?? (Array.isArray(pt) ? pt[0] : null);
        const v = pt.revisits ?? pt.value ?? pt.count ?? (Array.isArray(pt) ? pt[1] : 0);

        if (!ts) return { name: '-', val: 0 };
        const date = new Date((ts > 10_000_000_000 ? ts : ts * 1000));
        const name = date.toLocaleTimeString('en-GB', {
          hour: '2-digit', minute: '2-digit', hour12: false
          , timeZone: 'UTC'
        });
        return { name, val: Number(v) };
      });

      return (
        <div className={styles.chartContainer}>
          <div className={styles.chartTitle}>{chartTitle}</div>
          <div style={{ width: '100%', height: 200 }}>
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={chartData} margin={{ top: 10, right: 10, left: 30, bottom: 25 }}>
                <defs>
                  <linearGradient id="revisitCoverageGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#ccf54e" stopOpacity={0.4} />
                    <stop offset="95%" stopColor="#ccf54e" stopOpacity={0.0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(240, 240, 240, 0.05)" />
                <XAxis
                  dataKey="name"
                  tick={{ fill: '#b4b4b4', fontSize: 9, fontFamily: 'Space Grotesk' }}
                  label={{ value: xAxisLabel, position: 'insideBottom', offset: -10, fill: '#b4b4b4', fontSize: 10, fontFamily: 'Space Grotesk', fontWeight: 500 }}
                  dy={5}
                />
                <YAxis
                  tick={{ fill: '#b4b4b4', fontSize: 9, fontFamily: 'Space Grotesk' }}
                  label={{ value: yAxisLabel, angle: -90, position: 'insideLeft', offset: 0, style: { textAnchor: 'middle' }, fill: '#b4b4b4', fontSize: 10, fontFamily: 'Space Grotesk', fontWeight: 500 }}
                  dx={-5}
                />
                <Tooltip
                  contentStyle={{ background: '#090909', border: '0.5px solid #484848', borderRadius: 4, fontFamily: 'Montserrat' }}
                  labelStyle={{ color: '#eee', fontSize: 10 }}
                  itemStyle={{ color: '#ccf54e', fontSize: 10 }}
                  labelFormatter={(label) => `Time: ${label}`}
                  formatter={(value) => [`${value}`, 'Revisits']}
                />
                <Area
                  type="monotone"
                  dataKey={lineKey}
                  stroke="#ccf54e"
                  strokeWidth={1.5}
                  fill="url(#revisitCoverageGrad)"
                  dot={{ fill: '#ccf54e', r: 2 }}
                  activeDot={{ r: 4 }}
                />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>
      );
    }

    return null;
  };

  const renderRevisitIntervals = () => {
    return null;
  };

  const getTableTitle = () => {
    if (coverageSectionTab === 'simple') {
      const count = simpleCoverageData ? getPageMeta(simpleCoverageData).totalCount : 0;
      return `Coverage Windows (${count})`;
    }
    if (coverageSectionTab === 'gap') {
      const count = gapSummaryData ? getPageMeta(gapSummaryData).totalCount : 0;
      return `Gap Windows (${count})`;
    }
    if (coverageSectionTab === 'revisit') return '';

    return 'Sessions';
  };

  const renderCoverageDetailView = () => {
    if (!selectedCoverageReportDetail) return null;

    const aois = selectedCoverageReportDetail.raw?.requestInput?.aoiobjects ?? [];
    const satCount = selectedCoverageReportDetail.raw?.requestInput?.groups?.length ?? 0;
    const aoiCount = selectedCoverageReportDetail.raw?.requestInput?.aoiobjects?.length ?? 0;
    const gridSizeVal = selectedCoverageReportDetail.raw?.requestInput?.gridSize ?? '-';

    return (
      <div className={styles.detailView} ref={panelRef} style={{ display: 'flex', flexDirection: 'column', height: '100%', overflowY: 'auto' }}>
        {/* Legend modal disabled for now as requested */}
        <CoverageHeatmapLegendPanel
          metricMode={heatmapMetricMode}
          onMetricModeChange={setHeatmapMetricMode}
          visible={false}
          sectionTab={coverageSectionTab}
          dynamicBins={dynamicBins}
        />
        <div className={styles.detailHeader}>
          <div className={styles.detailTitleBlock}>
            <div className={styles.breadcrumb}>
              <button
                type="button"
                className={styles.breadcrumbLink}
                onClick={handleBackToList}
              >
                REPORTS
              </button>
              <span className={styles.breadcrumbSeparator}>/</span>
              <span className={styles.breadcrumbActive}>{selectedCoverageReportDetail.name}</span>
            </div>
            <div className={styles.detailTime} style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {selectedCoverageReportDetail.createdAt && (
                <div>{formatReportDate(selectedCoverageReportDetail.createdAt)}</div>
              )}

            </div>
          </div>

          <div className={styles.detailActions} style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
            <CustomToolTip title="Delete report" placement="bottom">
              <button
                type="button"
                className={styles.iconAction}
                disabled={deletingReportId === selectedCoverageReportDetail.reportId}
                onClick={() => setPendingDeleteReport({
                  id: selectedCoverageReportDetail.reportId,
                  name: selectedCoverageReportDetail.name || 'Coverage Report',
                  type: 'coverage',
                })}
              >
                <DeleteIcon />
              </button>
            </CustomToolTip>
            <CustomToolTip title="Close report" placement="bottom">
              <button
                type="button"
                className={styles.iconAction}
                onClick={handleBackToList}
              >
                <svg width="14" height="14" viewBox="0 0 14 14" fill="none"><path d="M3 3l8 8M11 3l-8 8" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" /></svg>
              </button>
            </CustomToolTip>
          </div>
        </div>

        {/* Inputs Summary Badge Pills */}
        <div className={styles.summaryBlock}>
          <span className={styles.summaryLabel}>Inputs</span>
          <div className={styles.summaryPills}>
            <div
              className={styles.summaryPill}
              title={`${satCount} Satellites`}
              onMouseEnter={() => setReportHoverHighlights(getCoverageSatHighlights(selectedCoverageReportDetail, satellites as Array<Satellite | SatGroup>))}
              onMouseLeave={() => setReportHoverHighlights(null)}
            >
              <div className={styles.pillIcon}><SatIcon /></div>
              <div className={styles.pillValue}>{satCount}</div>
            </div>
            <div
              className={styles.summaryPill}
              title={`${aoiCount} AOIs`}
              onMouseEnter={() => setReportHoverHighlights(getCoverageAoiHighlights(selectedCoverageReportDetail))}
              onMouseLeave={() => setReportHoverHighlights(null)}
            >
              <div className={styles.pillIcon}><GeoIcon /></div>
              <div className={styles.pillValue}>{aoiCount}</div>
            </div>
          </div>
        </div>

        {/* Output Section with 3 Custom Dropdowns (Geofeature, Satellites, Sensors) */}
        <div className={styles.outputBlock}>
          <div className={styles.outputHeader}>
            <span className={styles.outputLabel}>Output</span>
          </div>
          <div className={styles.outputFiltersRow}>
            <FigmaCustomSelect
              label="Geofeature"
              value={selectedAoiId || (aois[0]?.aoiId ?? '')}
              options={aois.map((aoi: any) => {
                const feature = geoFeatures.find(f => f.id === aoi.aoiId);
                return { value: aoi.aoiId, label: feature?.name ?? aoi.aoiId };
              })}
              onChange={(val) => {
                setSelectedAoiId(val);
                setCoveragePage(0);
              }}
            />
            <FigmaCustomSelect
              label="Satellites"
              value={selectedSatDropdownValue}
              options={[
                { value: 'All', label: 'All' },
                ...(satellites as Array<Satellite | SatGroup>)
                  .filter(s => usedEntities.satelliteIds.has(s.id) || usedEntities.groupIds.has(s.id))
                  .map(s => ({ value: s.id, label: s.name || s.id }))
              ]}
              onChange={(val) => {
                setSelectedSatDropdownValue(val);
                if (val === 'All') {
                  setSelectedSatFilter('');
                } else {
                  const item = (satellites as Array<Satellite | SatGroup>).find(s => s.id === val);
                  if (item && (item as SatGroup).type === 'group') {
                    const group = item as SatGroup;
                    const childIds = group.children.map(c => c.id).filter(Boolean);
                    setSelectedSatFilter(childIds.join(','));
                  } else {
                    setSelectedSatFilter(val);
                  }
                }
                setCoveragePage(0);
              }}
            />
            <FigmaCustomSelect
              label="Sensors"
              value="All"
              options={[{ value: 'All', label: 'All' }]}
              onChange={() => { }}
              disabled
            />
          </div>
        </div>

        {/* Figma 1:1 Section Tabs */}
        <div className={styles.figmaSectionTabs}>
          {['simple', 'gap', 'revisit']
            .filter(tab => !(tab === 'simple' && isTargetTrack))
            .map(tab => (
              <button
                key={tab}
                className={`${styles.figmaTabBtn} ${coverageSectionTab === tab ? styles.figmaTabActive : ''}`}
                type="button"
                onClick={() => { setCoverageSectionTab(tab as any); setCoveragePage(0); }}
              >
                {tab === 'simple' ? 'Simple Coverage' : tab === 'gap' ? 'Gap' : 'Revisits'}
              </button>
            ))}
        </div>

        {/* Overview KPIs */}
        {renderKPIs()}

        {/* Recharts curves */}
        {renderCoverageChart()}

        {/* Revisit intervals */}
        {renderRevisitIntervals()}

        {/* Table Title label */}
        {getTableTitle() && (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 12px 0 12px' }}>
            <div style={{ fontFamily: 'Montserrat, sans-serif', fontSize: 10, fontWeight: 400, color: 'rgba(240, 240, 240, 0.6)', letterSpacing: '0.6px', textTransform: 'uppercase' }}>
              {getTableTitle()}
            </div>
          </div>
        )}

        {/* Content table */}
        <div style={{ padding: '0 12px 16px 12px', flex: 1, display: 'flex', flexDirection: 'column' }}>
          {getTableTitle() && renderCoverageTable()}
          {getTableTitle() && renderCoveragePagination()}
        </div>
      </div>
    );
  };

  return (
    <aside
      className={`${styles.wrapper} ${open ? styles.active : ''} ${(selectedReportDetail || selectedCoverageReportDetail) ? styles.detailOpen : ''} ${resizing ? styles.resizing : ''} md-ai-cockpit-page`}
      style={{
        bottom: timelineOffset,
        '--report-panel-width': `${resolvedPanelWidth}px`,
      } as React.CSSProperties}
    >
      <div
        className={styles.resizeHandle}
        role="separator"
        aria-orientation="vertical"
        title="Resize report panel"
        onMouseDown={handleResizeStart}
      />
      <div className={`${styles.toggleContainer} ${hasProgress && !open ? styles.blinkAnimation : ''}`}>
        <CustomToolTip title="Analysis reports" placement="left">
          <button
            type="button"
            className={styles.toggleButton}
            onClick={handleToggleOpen}
            aria-label="Analysis reports"
          >
            <ReportPanelIcon />
          </button>
        </CustomToolTip>
      </div>
      {open && selectedReportDetail && aiDetached && (
        <div className={styles.detachedAiPanel}>
          {renderAIWorkspace(true)}
        </div>
      )}

      <div className={styles.container}>
        {(!selectedReportDetail && !selectedCoverageReportDetail) && (
          <div className={styles.header}>
            <div className={styles.tabsContainer}>
              <CustomToolTip title="Access Analysis Reports" placement="bottom">
                <button
                  className={`${styles.tabButton} ${reportsTab === 'access' ? styles.tabButtonActive : ''}`}
                  type="button"
                  onClick={() => setReportsTab('access')}
                  aria-label="Access Analysis Reports"
                >
                  <AccessAnalysisIcon />
                </button>
              </CustomToolTip>
              <CustomToolTip title="Coverage Analysis Reports" placement="bottom">
                <button
                  className={`${styles.tabButton} ${reportsTab === 'coverage' ? styles.tabButtonActive : ''}`}
                  type="button"
                  onClick={() => setReportsTab('coverage')}
                  aria-label="Coverage Analysis Reports"
                >
                  <CommandCoverageIcon />
                </button>
              </CustomToolTip>
            </div>
          </div>
        )}

        <div className={styles.body}>
          {selectedReportDetail ? (
            <div className={styles.detailView} ref={panelRef}>
              <div className={styles.detailHeader}>
                <div className={styles.detailTitleBlock}>
                  <div className={styles.breadcrumb}>
                    <button type="button" className={styles.breadcrumbLink} onClick={handleBackToList}>REPORTS</button>
                    <span className={styles.breadcrumbSeparator}>/</span>
                    <span className={styles.breadcrumbActive}>{getReportName(selectedReportDetail)}</span>
                  </div>
                  <div className={styles.detailTime} style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    {selectedReportDetail.createdAt && (
                      <div>{formatReportDate(selectedReportDetail.createdAt)}</div>
                    )}

                  </div>
                </div>
                <div className={styles.detailActions}>
                  <CustomToolTip title="Delete report" placement="bottom">
                    <button
                      type="button"
                      className={styles.iconAction}
                      aria-label={`Delete ${getReportName(selectedReportDetail)}`}
                      disabled={deletingReportId === selectedReportDetail.reportId}
                      onClick={() => setPendingDeleteReport({
                        id: selectedReportDetail.reportId,
                        name: getReportName(selectedReportDetail),
                        type: 'access',
                      })}
                    >
                      <DeleteIcon />
                    </button>
                  </CustomToolTip>
                  <CustomToolTip title="Close report" placement="bottom">
                    <button
                      type="button"
                      className={styles.iconAction}
                      onClick={handleBackToList}
                    >
                      <svg width="14" height="14" viewBox="0 0 14 14" fill="none"><path d="M3 3l8 8M11 3l-8 8" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" /></svg>
                    </button>
                  </CustomToolTip>
                </div>
              </div>

              {/* Inputs Summary Badge Pills (identical to Coverage Report) */}
              <div className={styles.summaryBlock}>
                <span className={styles.summaryLabel}>Inputs</span>
                <div className={styles.summaryPills}>
                  {detailInputCards.map(card => (
                    <div
                      key={card.key}
                      className={styles.summaryPill}
                      title={`${card.key}: ${card.count}`}
                      onMouseEnter={() => setInputCardHover(card)}
                      onMouseLeave={() => setInputCardHover(null)}
                    >
                      <div className={styles.pillIcon}>{card.icon}</div>
                      <div className={styles.pillValue}>{card.count}</div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Output Section with Custom Dropdowns and Advance Filter */}
              <div className={styles.outputBlock}>
                <div className={styles.outputHeader}>
                  <span className={styles.outputLabel}>Output</span>
                  <CustomFilter
                    alignment="right"
                    advanceFilter={{
                      filterIcon: <FilterSvg />,
                      filterList: filterList,
                      applyFilter: (filters: any) => {
                        setAllFilters(filters);
                        setSummaryData(null);
                        setEventsData(null);
                        setEventsPage(0);
                      },
                    }}
                    allFilters={allFilters}
                    setAllFilters={setAllFilters}
                    filterCase="report"
                    boundingScreen="md-ai-cockpit-page"
                  />
                </div>

              </div>

              {/* Figma 1:1 Section Tabs */}
              {!aiExpanded && (
                <div className={styles.figmaSectionTabs}>
                  {REPORT_TABS.map(tab => (
                    <button
                      key={tab}
                      type="button"
                      className={`${styles.figmaTabBtn} ${activeTab === tab ? styles.figmaTabActive : ''}`}
                      onClick={() => {
                        if (tab === 'Events' && activeTab !== 'Events') setEventsPage(0);
                        setActiveTab(tab);
                      }}
                    >
                      {tab}
                    </button>
                  ))}
                </div>
              )}

              {!aiExpanded && (
                <div className={styles.tabContent}>
                  {activeTab === 'Summary' && <SummaryContent data={summaryData} loading={tabLoading === 'Summary'} entityNames={entityNames} currentEpoch={currentEpoch} />}
                  {activeTab === 'Events' && (
                    <EventsContent
                      data={eventsData}
                      loading={tabLoading === 'Events'}
                      entityNames={entityNames}
                      allFilters={allFilters}
                      onPageChange={(page) => {
                        setEventsPage(page);
                      }}
                      currentEpoch={currentEpoch}
                    />
                  )}
                  {activeTab === 'Plots' && (
                    <PlotsContent
                      data={plotsData}
                      loading={tabLoading === 'Plots'}
                      groundStationOptions={plotGroundStationOptions}
                      selectedSatellitesByGS={selectedSatellitesByGS}
                      onSatelliteToggle={handleSatelliteToggle}
                      currentEpoch={currentEpoch}
                    />
                  )}
                </div>
              )}
              {!aiDetached && isMMAnalysisEnabled && renderAIWorkspace(false)}
            </div>
          ) : selectedCoverageReportDetail ? (
            renderCoverageDetailView()
          ) : reportsTab === 'access' ? (
            accessAnalysisReports.length === 0 ? (
              <div className={styles.message}>
                No reports to view. Please perform an Access Analysis to view the reports.
              </div>
            ) : (
              accessAnalysisReports.map((report, index) => {
                const selected = selectedReportId === report.reportId;
                const complete = isComplete(report);
                const failed = isFailed(report);
                const isPending = !complete;
                const reportName = getReportName(report, index);
                const input = getRequestInput(report);
                const inputCards = buildInputCards(input, geoFeatures as GeoFeature[], satellites as Array<Satellite | SatGroup>);

                const renderTime = () => {
                  if (loadingReportId === report.reportId) return 'Loading report...';
                  if (isPending) {
                    if (failed) {
                      return (
                        <span style={{ color: '#ff4d4d' }}>
                          ANALYSIS FAILED
                        </span>
                      );
                    }
                    const statusStr = String(report.status).toLowerCase();
                    const statusLabel = statusStr === 'queued' || statusStr === 'pending' ? 'ANALYSIS QUEUED...' : 'ANALYSIS IN PROGRESS...';
                    return (
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
                        <span className={styles.rowSpinner} />
                        {statusLabel}
                      </span>
                    );
                  }
                  const range = formatStartEnd(report);
                  if (range) return range;
                  return formatReportDate(report.createdAt);
                };

                return (
                  <article
                    key={report.reportId}
                    className={`${styles.reportContainer} ${selected ? styles.reportActive : ''} ${isPending ? styles.reportPending : ''}`}
                  >
                    <div className={styles.reportHeader}>
                      <button
                        type="button"
                        className={styles.reportHeaderDetails}
                        onClick={() => {
                          if (!isPending) handleSelectReport(report);
                        }}
                        style={{ cursor: isPending ? 'not-allowed' : 'pointer' }}
                      >
                        <span className={styles.reportTitle}>{reportName}</span>
                        <span className={styles.reportTime}>
                          {renderTime()}
                        </span>
                      </button>
                      <CustomToolTip title="Delete report" placement="bottom">
                        <button
                          type="button"
                          className={styles.deleteButton}
                          aria-label={`Delete ${reportName}`}
                          disabled={deletingReportId === report.reportId || isPending}
                          onClick={() => setPendingDeleteReport({
                            id: report.reportId,
                            name: reportName,
                            type: 'access',
                          })}
                          style={{
                            opacity: isPending ? 0.3 : undefined,
                            cursor: isPending ? 'not-allowed' : undefined
                          }}
                        >
                          <DeleteIcon />
                        </button>
                      </CustomToolTip>
                    </div>

                    <div className={styles.reportBody}>
                      <div className={styles.requestInputGrid}>
                        {inputCards.map(card => (
                          <div
                            key={card.key}
                            className={styles.inputSummaryCard}
                            onMouseEnter={() => setInputCardHover(card)}
                            onMouseLeave={() => setInputCardHover(null)}
                            onFocus={() => setInputCardHover(card)}
                            onBlur={() => setInputCardHover(null)}
                            tabIndex={card.count > 0 ? 0 : -1}
                          >
                            <div className={styles.inputIcon}>{card.icon}</div>
                            <div className={styles.inputCount}>{card.count}</div>
                          </div>
                        ))}
                      </div>
                      {!complete && !failed && (
                        <div className={styles.progressBlock} aria-label="Report generation in progress">
                          <span className={styles.progressBar} />
                        </div>
                      )}
                    </div>
                  </article>
                );
              })
            )
          ) : coverageAnalysisReports.length === 0 ? (
            <div className={styles.message}>
              No reports to view. Please perform a Coverage Analysis to view the reports.
            </div>
          ) : (
            coverageAnalysisReports.map((report, index) => {
              const selected = selectedCoverageReportId === report.reportId;
              const complete = report.status === 'completed';
              const failed = report.status === 'failed';
              const reportName = report.name || report.raw?.reportName || report.raw?.name || report.raw?.report_name || `COVERAGE_REPORT_${String(index + 1).padStart(2, '0')}`;

              const isPending = !complete;
              const satCount = report.raw?.requestInput?.groups?.length ?? 0;
              const aoiCount = report.raw?.requestInput?.aoiobjects?.length ?? 0;

              const renderTime = () => {
                if (loadingReportId === report.reportId) return 'Loading report...';
                if (isPending) {
                  if (failed) {
                    return (
                      <span style={{ color: '#ff4d4d' }}>
                        ANALYSIS FAILED
                      </span>
                    );
                  }
                  return (
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
                      <span className={styles.rowSpinner} />
                      ANALYSIS IN PROGRESS...
                    </span>
                  );
                }
                const range = formatStartEnd(report);
                if (range) return range;
                return formatReportDate(report.createdAt);
              };

              return (
                <article
                  key={report.reportId}
                  className={`${styles.reportContainer} ${selected ? styles.reportActive : ''} ${isPending ? styles.reportPending : ''}`}
                >
                  <div className={styles.reportHeader}>
                    {renamingCoverageReportId === report.reportId ? (
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6, flex: 1 }}>
                        <input
                          style={{ flex: 1, height: 22, background: 'rgba(9, 9, 9, 0.8)', border: '0.5px solid rgba(240,240,240,0.3)', borderRadius: 3, color: '#f0f0f0', padding: '0 4px', fontSize: 11 }}
                          value={renamingName}
                          onChange={(e) => setRenamingName(e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') handleRenameCoverage(report.reportId);
                            if (e.key === 'Escape') setRenamingCoverageReportId(null);
                          }}
                          autoFocus
                        />
                        <button
                          type="button"
                          onClick={() => handleRenameCoverage(report.reportId)}
                          style={{ background: 'transparent', border: 'none', color: '#a3e635', cursor: 'pointer', fontSize: 11 }}
                        >
                          ✓
                        </button>
                        <button
                          type="button"
                          onClick={() => setRenamingCoverageReportId(null)}
                          style={{ background: 'transparent', border: 'none', color: 'rgb(255,100,100)', cursor: 'pointer', fontSize: 11 }}
                        >
                          ✕
                        </button>
                      </div>
                    ) : (
                      <button
                        type="button"
                        className={styles.reportHeaderDetails}
                        onClick={() => {
                          if (!isPending) handleSelectCoverageReport(report);
                        }}
                        style={{ cursor: isPending ? 'not-allowed' : 'pointer' }}
                      >
                        <span className={styles.reportTitle}>{reportName}</span>
                        <span className={styles.reportTime}>
                          {renderTime()}
                        </span>
                      </button>
                    )}

                    {renamingCoverageReportId !== report.reportId && (
                      <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                        <CustomToolTip title="Rename report" placement="bottom">
                          <button
                            type="button"
                            className={styles.deleteButton}
                            disabled={isPending}
                            onClick={(e) => {
                              if (isPending) return;
                              e.stopPropagation();
                              setRenamingCoverageReportId(report.reportId);
                              setRenamingName(reportName);
                            }}
                            style={{
                              background: 'transparent',
                              border: 'none',
                              color: '#f0f0f0',
                              opacity: isPending ? 0.2 : 0.6,
                              cursor: isPending ? 'not-allowed' : 'pointer',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              padding: 2,
                            }}
                          >
                            <EditIcon />
                          </button>
                        </CustomToolTip>
                        <CustomToolTip title="Delete report" placement="bottom">
                          <button
                            type="button"
                            className={styles.deleteButton}
                            aria-label={`Delete ${reportName}`}
                            disabled={deletingReportId === report.reportId || isPending}
                            onClick={() => setPendingDeleteReport({
                              id: report.reportId,
                              name: reportName,
                              type: 'coverage',
                            })}
                            style={{
                              opacity: isPending ? 0.3 : undefined,
                              cursor: isPending ? 'not-allowed' : undefined,
                            }}
                          >
                            <DeleteIcon />
                          </button>
                        </CustomToolTip>
                      </div>
                    )}
                  </div>

                  <div className={styles.reportBody}>
                    <div className={styles.requestInputGrid}>
                      <div
                        className={styles.inputSummaryCard}
                        onMouseEnter={() => setReportHoverHighlights(getCoverageSatHighlights(report, satellites as Array<Satellite | SatGroup>))}
                        onMouseLeave={() => setReportHoverHighlights(null)}
                        onFocus={() => setReportHoverHighlights(getCoverageSatHighlights(report, satellites as Array<Satellite | SatGroup>))}
                        onBlur={() => setReportHoverHighlights(null)}
                        tabIndex={satCount > 0 ? 0 : -1}
                      >
                        <div className={styles.inputIcon}><SatelliteIcon /></div>
                        <div className={styles.inputCount}>{satCount}</div>
                      </div>
                      <div
                        className={styles.inputSummaryCard}
                        onMouseEnter={() => setReportHoverHighlights(getCoverageAoiHighlights(report))}
                        onMouseLeave={() => setReportHoverHighlights(null)}
                        onFocus={() => setReportHoverHighlights(getCoverageAoiHighlights(report))}
                        onBlur={() => setReportHoverHighlights(null)}
                        tabIndex={aoiCount > 0 ? 0 : -1}
                      >
                        <div className={styles.inputIcon}><PolygonIcon /></div>
                        <div className={styles.inputCount}>{aoiCount}</div>
                      </div>
                    </div>
                    {!complete && !failed && (
                      <div className={styles.progressBlock} aria-label="Report generation in progress">
                        <span className={styles.progressBar} />
                      </div>
                    )}
                  </div>
                </article>
              );
            })
          )}
        </div>
      </div>

      {ReactDOM.createPortal(
        <ConfirmationDialog
          type="delete"
          open={pendingDeleteReport !== null}
          title={`Delete ${pendingDeleteReport?.type === 'coverage' ? 'Coverage' : 'Access'} Report?`}
          loading={deletingReportId !== null}
          description={
            pendingDeleteReport
              ? `Deleting "${pendingDeleteReport.name}" will permanently remove it from the database, and it cannot be restored once deleted.`
              : ''
          }
          confirm_button_text="Delete"
          onConfirm={handleConfirmDeleteReport}
          onCancel={() => setPendingDeleteReport(null)}
        />,
        document.body
      )}
    </aside>
  );
};

export default AccessAnalysisReportsPanel;
